import { useCallback, useEffect, useMemo, useState } from "react";
import ColumnFilterGrid from "./ColumnFilterGrid";
import CollapsibleSection from "./CollapsibleSection";
import MatchDetailTable from "./MatchDetailTable";
import MatchGroupRow from "./MatchGroupRow";
import MatchListTable from "./MatchListTable";
import ConfirmDangerAction from "./ConfirmDangerAction";
import ForgetUndoBanner from "./ForgetUndoBanner";
import InfoTip from "./InfoTip";
import { fmtAed } from "./KpiCard";
import PriorPeriodMatches from "./PriorPeriodMatches";
import { api, ApiError } from "../api/client";
import { useToast } from "./Toast";
import { isSettledBeforeMatch, partialIdenticalCopies, sigAmount, sigLabel, sigSide } from "../lib/manualMatchLabels";
import { sigPosMap } from "../lib/columns";
import { SIGN_MISMATCH_NOTE, UNLISTED_LEG_NOTE, handMatchResidual, hasAfterPeriodLeg, hasUnlistedLeg,
         offBucketReasons } from "../lib/handMatchSign";
import { countAmount, countPolicy, policyTarget, type PolicyTarget } from "../lib/ledgerView";
import { buildSuggestionFrame, pairKeyOf, splitContested } from "../lib/suggestionFrame";
import type { MatchListEntry } from "../lib/matchListFrame";
import { buildSavedMatches, savedMatchBadge, type SavedMatch } from "../lib/savedMatches";
import { applyFramesDelta } from "../lib/framesDelta";
import { autoClearings } from "../lib/clearedRelease";
import { useMatchWorkspace } from "../lib/useMatchWorkspace";
import {
  CARRIED_AMOUNTS, CARRIED_SIG, amountFor as carriedAmountFor, carriedConfig, carriedCopies,
  carriedRows, carriedSourceColumns, isPriorSig, judgePriorBasket, ledgerCopies,
  priorSettlementOf, withPriorOthers, withPriorRows, withSourceColumn, withoutRows, type Book,
  type PriorSettlement,
} from "../lib/priorPeriodMatch";
import { groupSigs, isSameBookPair, pairKey } from "../types/api";
import type { CarriedMatchPair, FrameRecords, ManualMatchPair, ManualRolled, ManualUnresolved, NearMiss, ReconResult, ReleasedGroup, SaveCheck, SideDisplayConfig } from "../types/api";
import SaveMatchDialog, { type SaveAnswers } from "./SaveMatchDialog";

interface Props {
  result: ReconResult;
  prematchId: string;
  insurerName: string;
  sessionPairs: ManualMatchPair[];
  // The frames as /reconcile produced them, BEFORE any manual match was applied. The
  // delta response only carries Tier-6 rows, so patching must always start from this
  // pristine copy — that's what lets a REMOVED match revert its rows (they're simply
  // absent from the new delta). Never the currently-displayed (already-patched) result.
  baseline: ReconResult;
  // `full` = the result carries COMPLETE frames, so the page adopts it as the new baseline.
  onApplied: (result: ReconResult, pairs: ManualMatchPair[], full?: boolean) => void;
  // The amount_match_tol the current result was computed with: the ONE tolerance the
  // engine's manual-match gate uses, on every track (the API sets it to the Premium
  // tolerance). Lets the match bar say what the engine will do. Omitted -> no verdict.
  amountTol?: number;
  // Re-runs the reconciliation after the server's saved session expired; resolves the
  // new prematch id to retry against.
  onPrematchExpired?: () => Promise<string>;
  // Re-runs the reconciliation after a prior-period pair is saved: the Adjusted Closing
  // is an ENGINE figure, so the only honest way to reflect the pair is to re-run.
  onCarriedPairsChanged?: () => Promise<void> | void;
}

// A gap under half a cent rounds to "0.00", which against a 0.00 tolerance read as
// "residual 0.00 — outside the 0.00 tolerance". Show such a gap's own digits instead.
function fmtResidual(v: number): string {
  if (!(v > 0 && v < 0.005)) return fmtAed(v);
  const s = v.toFixed(6).replace(/0+$/, "");
  return s.endsWith(".") ? "<0.000001" : s;
}

// The amount of the row with the given signature, or null. Lets the pair preview show
// both amounts (and their gap) BEFORE the user commits the match.
// Through the cached sig->position map, not frame.rows.find: this is called once per
// SELECTED signature inside the `picked` memo, which recomputes on every tick — so
// "Select all N matching rows" over a large ledger was N full scans, then N again on the
// next tick. Same first-position-wins rule the find had.
function amountFor(frame: FrameRecords, sig: string | null, amountCol: string): number | null {
  if (!sig) return null;
  const ai = frame.columns.indexOf(amountCol);
  if (ai < 0 || frame.columns.indexOf("_sig") < 0) return null;
  const pos = sigPosMap(frame).get(sig);
  if (pos === undefined) return null;
  const v = frame.rows[pos][ai];
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

// The track of the row with the given signature ("" when absent), for the sign rule.
function trackFor(frame: FrameRecords, sig: string): unknown {
  const ti = frame.columns.indexOf("track");
  if (ti < 0 || frame.columns.indexOf("_sig") < 0) return "";
  const pos = sigPosMap(frame).get(sig);
  return pos === undefined ? "" : frame.rows[pos][ti];
}

// Slice a frame down to its unmatched rows (match_status "In CRI, not Insurer" for CRI,
// "In Insurer, not CRI" for insurer) — the rows eligible for manual pairing.
function unmatchedSlice(frame: FrameRecords, status: string): FrameRecords {
  const i = frame.columns.indexOf("match_status");
  if (i < 0) return frame;
  const rows: FrameRecords["rows"] = [];
  const indices: FrameRecords["indices"] = [];
  frame.rows.forEach((r, ri) => {
    if (String(r[i] ?? "") === status) {
      rows.push(r);
      indices.push(frame.indices[ri]);
    }
  });
  return { columns: frame.columns, rows, indices, total_rows: rows.length };
}

// Manual Match tab — mirrors app/overrides.py::_render_manual_match_tab: two side-by-side
// column-filterable grids (CRI unmatched | Insurer unmatched), single-select each, a
// "Match selected pair" button that adds a Tier-6 pair and re-finalizes (instant), and
// the one saved-matches list with Forget.
export default function ManualMatchTab({ result, prematchId, insurerName, sessionPairs, baseline, onApplied, amountTol, onPrematchExpired, onCarriedPairsChanged }: Props) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // THE SELECTION IS ONE BASKET, not a mode. Rows are ticked in either grid (or both)
  // and the KIND of match is inferred from what was picked: 1 CRI + 1 insurer is the
  // classic cross-book pair, N + M is a many-to-many group, and 2+ rows in ONE grid is
  // a within-ledger offset. Click order is preserved so the basket reads back the way
  // the user built it.
  const [criPicks, setCriPicks] = useState<string[]>([]);
  const [insPicks, setInsPicks] = useState<string[]>([]);
  const toggle = (setter: typeof setCriPicks) => (sig: string | null) => {
    if (!sig) return;
    setError("");
    setter((prev) => prev.includes(sig) ? prev.filter((s) => s !== sig) : [...prev, sig]);
  };
  const toggleCri = toggle(setCriPicks);
  const toggleIns = toggle(setInsPicks);

  // Bulk add/remove of every row a search or filter surfaced. Adding preserves the
  // existing basket and click order and appends only what is genuinely new, so ticking
  // a searched set twice can't duplicate a leg (buildGroup dedupes too, but the basket
  // the user reads must be right on its own).
  const selectMany = (setter: typeof setCriPicks) => (sigs: string[], selected: boolean) => {
    setError("");
    setter((prev) => {
      if (!selected) {
        const drop = new Set(sigs);
        return prev.filter((s) => !drop.has(s));
      }
      const have = new Set(prev);
      return [...prev, ...sigs.filter((s) => !have.has(s))];
    });
  };
  const selectManyCri = selectMany(setCriPicks);
  const selectManyIns = selectMany(setInsPicks);

  function clearPicks() {
    setCriPicks([]);
    setInsPicks([]);
    setError("");
  }

  // Set form for the grids' O(1) "is this row ticked" lookup.
  const criPickSet = useMemo(() => new Set(criPicks), [criPicks]);
  const insPickSet = useMemo(() => new Set(insPicks), [insPicks]);

  const criUnmatched = useMemo(() => unmatchedSlice(result.df_cri, "In CRI, not Insurer"), [result.df_cri]);
  const insUnmatched = useMemo(() => unmatchedSlice(result.df_insurer, "In Insurer, not CRI"), [result.df_insurer]);

  // PRIOR-PERIOD ITEMS SHARE THE BOOKS (finance 2026-10-08): each grid holds this period's
  // unmatched rows and that book's still-open carried items, marked by a Source column,
  // so finance never picks a list or a target first. A basket holding a carried item is
  // judged and saved by the prior-period rules (lib/priorPeriodMatch); one without is the
  // main manual match, unchanged. A book with no open carried item is the slice itself.
  // Plus the previous output's Others rows, matched by hand like any item (withPriorOthers).
  const prior = useMemo(() => withPriorOthers(result.unreconciled, result.prior_others),
    [result.unreconciled, result.prior_others]);
  // A cumulative statement re-prints a prior-period item before the window, so the same
  // transaction sat in the book twice (finance 2026-10-10). The book offers the item; its
  // re-print stays one click away, never gone (the engine names it: ledgerCopies).
  const [showReprints, setShowReprints] = useState(false);
  const criNoReprints = useMemo(() => withoutRows(criUnmatched, ledgerCopies(carriedRows(prior, "cri"))),
    [criUnmatched, prior]);
  const insNoReprints = useMemo(() => withoutRows(insUnmatched, ledgerCopies(carriedRows(prior, "insurer"))),
    [insUnmatched, prior]);
  const criReprintN = criUnmatched.total_rows - criNoReprints.total_rows;
  const insReprintN = insUnmatched.total_rows - insNoReprints.total_rows;
  const criShown = showReprints ? criUnmatched : criNoReprints;
  const insShown = showReprints ? insUnmatched : insNoReprints;
  const criBook = useMemo(() => withPriorRows(criShown, carriedRows(prior, "cri"),
    carriedSourceColumns(prior, "cri"), "cri_amount", result.display_config?.cri),
    [criShown, prior, result.display_config]);
  const insBook = useMemo(() => withPriorRows(insShown, carriedRows(prior, "insurer"),
    carriedSourceColumns(prior, "insurer"), "balance_amount", result.display_config?.insurer),
    [insShown, prior, result.display_config]);
  const criPriorN = criBook.total_rows - criShown.total_rows;
  const insPriorN = insBook.total_rows - insShown.total_rows;
  const reprintToggle = (n: number) => n > 0 && (
    <> · <button type="button" className="btn btn-xs" onClick={() => setShowReprints((v) => !v)}
      title="Pre-period ledger rows that are the same document (reference, amount, date) as a prior-period item in this book. Match the prior-period item instead.">
      {showReprints ? "Hide" : "Show"} {n} re-print{n === 1 ? "" : "s"}
    </button></>);
  const criBookConfig = criPriorN ? withSourceColumn(result.display_config?.cri) : result.display_config?.cri;
  const insBookConfig = insPriorN ? withSourceColumn(result.display_config?.insurer) : result.display_config?.insurer;
  // A carried item is named by its own file's columns, as on the Prior-Period tab.
  const priorConfigs = useMemo(() => ({
    cri: carriedConfig(carriedRows(prior, "cri"), carriedSourceColumns(prior, "cri")),
    insurer: carriedConfig(carriedRows(prior, "insurer"), carriedSourceColumns(prior, "insurer")),
  }), [prior]);

  // A re-finalize can match a ticked row elsewhere (an accepted suggestion, a restore).
  // Left in the basket it counts as 0, and Match would save a pair the engine refuses, so
  // the basket keeps only rows still open. Frames not loaded yet carry no _sig: kept as is.
  useEffect(() => {
    const keep = (frame: FrameRecords) => (prev: string[]) => {
      if (!prev.length || frame.columns.indexOf("_sig") < 0) return prev;
      const open = sigPosMap(frame);
      const next = prev.filter((s) => open.has(s));
      return next.length === prev.length ? prev : next;
    };
    setCriPicks(keep(criBook));
    setInsPicks(keep(insBook));
  }, [criBook, insBook]);

  // What the grids' search/filter state survives. It must change when the LEDGER changes
  // (a new upload, a different insurer — a stale query there would describe other books)
  // and NOT when the same ledger is merely re-finalized. prematchId is precisely that
  // line: one prematch is one pair of parsed ledgers, and every manual match, release,
  // restore and note re-finalizes against it without changing it.
  //
  // Without this the grids keyed on frame IDENTITY, which a re-finalize always replaces.
  // Releasing one pair from the matched-hits panel therefore wiped the pasted search that
  // produced the panel, so every other entry in it disappeared in the same instant — and
  // finance read that as the one click having released all of them.
  const gridResetKey = `${insurerName}|${prematchId}`;

  // What the current basket adds up to, and whether it will tie. MIRRORS the engine's
  // group tie gate in backend.reconcile._apply_manual_matches exactly — cross-book legs
  // must cancel under result.manual_match_rules (handMatchResidual), within-ledger nets the
  // signed amounts — so the preview can never promise an outcome the engine won't produce.
  const signRules = result.manual_match_rules;
  const picked = useMemo(() => {
    // Always the SIGNED net of the side. Cross-book then compares |net| to |net| (see
    // below) — never a sum of per-row magnitudes, which double-counts a side that holds
    // both a debit and a credit and invents a gap the rows do not have.
    const sum = (frame: FrameRecords, sigs: string[], col: string) =>
      sigs.reduce((t, s) => {
        const v = amountFor(frame, s, col);
        return t + (v === null ? 0 : v);
      }, 0);
    const nc = criPicks.length;
    const ni = insPicks.length;
    const total = nc + ni;
    const crossBook = nc > 0 && ni > 0;
    const criNet = sum(criBook, criPicks, "cri_amount");
    const insNet = sum(insBook, insPicks, "balance_amount");
    // Cross-book displays each side at its magnitude (the two books carry opposite
    // signs for the same document), within-ledger keeps the signed net so the user can
    // see the legs cancelling.
    const criTotal = crossBook ? Math.abs(criNet) : criNet;
    const insTotal = crossBook ? Math.abs(insNet) : insNet;
    const magnitudeGap = Math.abs(criTotal - insTotal);
    const gap = crossBook
      ? handMatchResidual(criPicks.map((s) => amountFor(criBook, s, "cri_amount") ?? 0),
                          insPicks.map((s) => amountFor(insBook, s, "balance_amount") ?? 0),
                          insPicks.map((s) => trackFor(insBook, s)), signRules)
      : magnitudeGap;
    // Same amount, signs that do not cancel: Group 3, and the bar says why.
    const signMismatch = crossBook && Math.round(magnitudeGap * 100) / 100 <= 0.005
      && Math.round(gap * 100) / 100 > 0.005;
    const net = criTotal + insTotal;
    // The residual the engine will gate on: the two sides' difference (cross-book) or
    // the signed net (within-ledger), in whole cents as _apply_manual_matches judges it,
    // so summation-order float noise can never flip the verdict.
    const residual = Math.round((crossBook ? gap : Math.abs(net)) * 100) / 100;
    const ties = residual <= 0.005;
    // The engine's verdict, when the tolerance is known: "Matched" iff residual <=
    // amount_match_tol.
    const outcome: "match" | "mismatch" | null = amountTol === undefined ? null
      : residual <= amountTol ? "match" : "mismatch";
    const kind = total >= 2 ? (crossBook ? "cross" : "same") : null;
    return {
      total, crossBook, criTotal, insTotal, gap, net, residual, ties, outcome, kind, signMismatch,
      sameBookLabel: ni > 0 ? "Insurer" : "CRI",
      kindLabel: crossBook
        ? (nc === 1 && ni === 1 ? "Cross-ledger pair" : `Cross-ledger ${nc}:${ni} group`)
        : `Within ${ni > 0 ? "Insurer" : "CRI"} offset · ${total} rows`,
    };
  }, [criPicks, insPicks, criBook, insBook, amountTol, signRules]);
  // A basket the engine would refuse (a balance row, or a journal in a match settled after
  // the period) is not saved. A tying basket with a leg after the period routes to Cleared.
  const offBucket = useMemo(() => {
    const ties = picked.outcome ? picked.outcome === "match" : picked.ties;
    const inPeriod = !(ties && (hasAfterPeriodLeg(criBook, criPicks)
                                || hasAfterPeriodLeg(insBook, insPicks)));
    const unlisted = result.unlisted_pre_period_rows;
    const settlesUnlisted = picked.crossBook && ties && inPeriod
      && (hasUnlistedLeg(criBook, criPicks, unlisted?.cri)
          || hasUnlistedLeg(insBook, insPicks, unlisted?.insurer));
    return [
      ...offBucketReasons(criBook, criPicks, "cri", signRules, inPeriod),
      ...offBucketReasons(insBook, insPicks, "insurer", signRules, inPeriod),
      ...(settlesUnlisted ? [UNLISTED_LEG_NOTE] : []),
    ];
  }, [criBook, criPicks, insBook, insPicks, signRules, picked.outcome, picked.ties,
      picked.crossBook, result.unlisted_pre_period_rows]);

  // The verdict for a basket holding a prior-period item, or null (the main match above).
  const pv = useMemo(() => judgePriorBasket({
    criPicks, insPicks, block: prior, criLedger: criUnmatched, insLedger: insUnmatched,
    amountTol: amountTol ?? prior?.manual_rules?.tol ?? 0,
    unlisted: result.unlisted_pre_period_rows, latePosted: result.late_posted_pre_period_rows,
  }), [criPicks, insPicks, prior, criUnmatched, insUnmatched, amountTol,
      result.unlisted_pre_period_rows, result.late_posted_pre_period_rows]);

  // "Find this amount in the other book": on request only, the OTHER grid narrows to rows
  // carrying the ticked side's amount (its net, for several rows) on either sign. It
  // answers one selection, so it drops as soon as that side's ticks change; ticking the
  // partner in the narrowed book leaves it in place.
  const [amountFind, setAmountFind] = useState<{ side: "cri" | "insurer"; target: number; source: string } | null>(null);
  const criKey = criPicks.join("\u0001");
  const insKey = insPicks.join("\u0001");
  const findFrom = criPicks.length && !insPicks.length ? "cri"
    : insPicks.length && !criPicks.length ? "insurer" : null;
  const findTarget = findFrom === "cri" ? Math.abs(picked.criTotal)
    : findFrom === "insurer" ? Math.abs(picked.insTotal) : 0;
  useEffect(() => {
    setAmountFind((f) => (f && f.source !== (f.side === "insurer" ? criKey : insKey) ? null : f));
  }, [criKey, insKey]);
  // Rows the find would show, over the other book's WHOLE unmatched slice (finance
  // 2026-09-28: offer the button only when the amount is actually there).
  const findHits = useMemo(() => {
    if (!findFrom || findTarget < 0.005) return 0;
    return findFrom === "cri" ? countAmount(insBook, "balance_amount", findTarget)
      : countAmount(criBook, "cri_amount", findTarget);
  }, [findFrom, findTarget, criBook, insBook]);

  // "Find this policy in the other book": the same on-request rules as the amount find,
  // offered only when every ticked row has one policy and the other book carries it.
  // Both finds can be active together; the grid then shows rows meeting both.
  const [policyFind, setPolicyFind] = useState<{ side: "cri" | "insurer"; target: PolicyTarget; source: string } | null>(null);
  useEffect(() => {
    setPolicyFind((f) => (f && f.source !== (f.side === "insurer" ? criKey : insKey) ? null : f));
  }, [criKey, insKey]);
  const policyTgt = useMemo(() => {
    if (!findFrom) return null;
    const frame = findFrom === "cri" ? criBook : insBook;
    const picks = findFrom === "cri" ? criPicks : insPicks;
    const at = sigPosMap(frame);
    const pos = picks.map((s) => at.get(s)).filter((p): p is number => p !== undefined);
    return pos.length === picks.length ? policyTarget(frame, pos) : null;
  }, [findFrom, criPicks, insPicks, criBook, insBook]);
  const policyHits = useMemo(() => {
    if (!policyTgt) return 0;
    return countPolicy(findFrom === "cri" ? insBook : criBook, policyTgt);
  }, [policyTgt, findFrom, criBook, insBook]);

  // Ticked rows that have identical copies in their book which are not all ticked.
  const partialCopies = useMemo(() => [
    ...partialIdenticalCopies(result.df_cri, criPicks).map((c) => ({ ...c, side: "CRI" as const })),
    ...partialIdenticalCopies(result.df_insurer, insPicks).map((c) => ({ ...c, side: "Insurer" as const })),
  ], [result.df_cri, result.df_insurer, criPicks, insPicks]);
  // Ticked prior-period items with an identical copy on their list, which a saved match
  // cannot tell apart: it always attaches to the FIRST of them.
  const priorCopies = useMemo(() => (["cri", "insurer"] as const).flatMap((b) =>
    carriedCopies(carriedRows(prior, b), (b === "cri" ? criPicks : insPicks).filter(isPriorSig))
      .map((c) => ({ ...c, book: b }))), [prior, criPicks, insPicks]);

  // Suggested matches (engine near-misses): pairs sharing a real reference/policy key
  // and reconciling sign, left open only on the amount. Reject is session-only (dismiss
  // from view); Accept routes through the same applyPair flow as a manual match.
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const suggestions = useMemo(() => {
    const nm = (result.near_misses ?? []) as NearMiss[];
    return nm.filter((s) => !dismissed.has(`${s.cri_sig}⟷${s.insurer_sig}`));
  }, [result.near_misses, dismissed]);
  // Suggestions are sorted closest-gap-first by the engine, so a progressive reveal
  // surfaces the highest-confidence pairs first. Default collapsed (the queue is an
  // advisory shortcut, not the primary workflow); show a first batch and let the user
  // reveal more without hiding rows behind Accept.
  const SUGGEST_BATCH = 15;
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestShown, setSuggestShown] = useState(SUGGEST_BATCH);
  // Which suggestion rows are expanded to their full transaction detail (keyed by pair sig).
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  // Ticked suggestions, for accepting several in ONE re-finalize (keyed by pair sig).
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggleSelected = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  // Bulk tick/untick from the table view's "Select all N matching rows", which acts on the
  // whole FILTERED set. Mirrors the ledger grids' selectMany so the two behave identically.
  const selectManySuggestions = (keys: string[], on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const k of keys) on ? next.add(k) : next.delete(k);
      return next;
    });
  // Only the VISIBLE suggestions can be ticked, so selection can't silently carry rows
  // the user never saw (a rejected or scrolled-away pair drops out on its own).
  const visibleSuggestions = useMemo(
    () => suggestions.slice(0, suggestShown), [suggestions, suggestShown]);
  const selectedVisible = useMemo(
    () => visibleSuggestions.filter((s) => selected.has(pairKeyOf(s))),
    [visibleSuggestions, selected]);

  // LIST vs TABLE. Two renderings of ONE queue and ONE selection set — never two places
  // holding their own ticks, which is the failure mode a separate tab would have invited.
  // The table is the default once the queue is big enough that scrolling a list is the
  // wrong tool: at 264 suggestions (real, Dubai) the list needs 18 "Show more" clicks
  // before "select all shown" even sees the whole queue.
  const TABLE_DEFAULT_FROM = 25;
  const [suggestView, setSuggestView] = useState<"list" | "table">(
    suggestions.length >= TABLE_DEFAULT_FROM ? "table" : "list");

  // The table's frame: one row per PAIR, carrying every raw column of both legs. Rebuilt
  // only when the queue or the ledgers change — not on a tick, which must stay instant.
  const suggestionGrid = useMemo(
    () => buildSuggestionFrame(suggestions, criUnmatched, insUnmatched),
    [suggestions, criUnmatched, insUnmatched]);

  // In TABLE view the bulk actions act on everything ticked (the user filtered precisely
  // to reach it), not on a reveal window the table doesn't have. Resolved back to the
  // NearMiss objects so Accept/Reject take the same path in both views.
  // What the TABLE's search and column filters leave on screen (null = unfiltered). Accept,
  // Reject and the select-all box act on these only: a filtered table must never accept or
  // hide a suggestion the user cannot see.
  const [suggestFiltered, setSuggestFiltered] = useState<string[] | null>(null);
  const tableShown = useMemo(() => {
    if (!suggestFiltered) return suggestions;
    const keep = new Set(suggestFiltered);
    return suggestions.filter((s) => keep.has(pairKeyOf(s)));
  }, [suggestions, suggestFiltered]);
  const selectedAll = useMemo(
    () => tableShown.filter((s) => selected.has(pairKeyOf(s))),
    [tableShown, selected]);
  const selectedForAction = suggestView === "table" ? selectedAll : selectedVisible;
  // What the card's own select-all box covers: what the table shows (the whole queue when
  // unfiltered), or the revealed window in the list. Kept in step with selectedForAction so
  // the box, the indeterminate state and the Accept/Reject counts describe the same set.
  const selectableSuggestions = suggestView === "table" ? tableShown : visibleSuggestions;
  const hiddenTickedSuggestions = suggestView === "table"
    ? suggestions.filter((s) => selected.has(pairKeyOf(s))).length - selectedAll.length : 0;
  const allSelectableSelected = selectableSuggestions.length > 0
    && selectedForAction.length === selectableSuggestions.length;

  // Auto-matches finance has RELEASED back to the open buckets so they can be paired by
  // hand. Persisted per insurer, so a pair the matcher keeps getting wrong does not have
  // to be released again every month; fully reversible via Restore.
  const [released, setReleased] = useState<ReleasedGroup[]>([]);
  // Tracked, not swallowed, for the reason the remembered-matches load below spells out:
  // the server applies a stored release on every run whether or not this list loaded, so
  // a failed fetch must read as an ERROR - an empty list hid the panel and its Restore.
  const [releasedError, setReleasedError] = useState("");
  useEffect(() => {
    let stale = false;
    setReleasedError("");
    api.releasedMatches(insurerName)
      .then((r) => { if (!stale) setReleased(r.released); })
      .catch((e) => {
        if (stale) return;
        setReleased([]);
        setReleasedError(String(e));
      });
    return () => { stale = true; };
  }, [insurerName]);

  // Each manual match APPLIED this run, by every one of its legs — what the
  // found-in-matched panel needs to offer Unmatch, since a manual match is applied after
  // releases and Release can never reach it. Read off the engine's own records, so it is
  // exactly what is stamped, whether the pair came from this session or the store.
  // A match saved under a retired row key names its rows by that key; the grids know a row
  // by its CURRENT key (cri_keys / insurer_keys), so both map to the match. Without the
  // current ones such a match read as automatic and offered Release, which never undoes it.
  const manualByLeg = useMemo(() => {
    const m = new Map<string, ManualMatchPair>();
    for (const rec of (result.manual_matched as ManualMatchPair[] | undefined) ?? []) {
      const { cri, ins } = groupSigs(rec);
      for (const s of [...cri, ...ins, ...(rec.cri_keys ?? []), ...(rec.insurer_keys ?? [])]) {
        m.set(s, rec);
      }
    }
    return m;
  }, [result]);
  // The legs as the grids know them, so the panel draws each row of the match.
  const manualGroupOf = useCallback((sig: string) => {
    const rec = manualByLeg.get(sig);
    if (!rec) return undefined;
    const { cri, ins } = groupSigs(rec);
    return [...(rec.cri_keys ?? cri), ...(rec.insurer_keys ?? ins)];
  }, [manualByLeg]);

  // Undo matches from the found-in-matched panel, in ONE re-finalize: `groups` are
  // auto-matches to release (one stored entry per match, so each restores on its own),
  // `unmatchSigs` one leg of each manual match to unmatch — removed from this session and
  // from the remembered store, or it would re-apply on the next upload. `named` labels a
  // single release in the toast. `priorUndo` are prior-period pairs, undone through their
  // item: an automatic clear rejected, a hand pair forgotten.
  async function undoMatches(groups: string[][], unmatchSigs: string[], named?: string,
                             priorUndo: PriorSettlement[] = []) {
    setBusy(true);
    setError("");
    const errors: string[] = [];
    // Sequential, not parallel: every response is the whole store, and only the LAST
    // write's response is guaranteed to hold all of them.
    let stored: ReleasedGroup[] = released;
    const sent: string[][] = [];
    let releaseWhy = "";
    for (const sigs of groups) {
      try {
        const r = await api.releaseMatch(insurerName, sigs, "released from manual match");
        stored = r.released;
        setReleased(r.released);
        setReleasedError("");
        sent.push(sigs);
      } catch (e) {
        releaseWhy ||= e instanceof Error ? e.message : String(e);   // counted below
      }
    }
    if (sent.length < groups.length) {
      errors.push(`could not release ${groups.length - sent.length} of ${groups.length} pair${
        groups.length === 1 ? "" : "s"}: ${releaseWhy}`);
    }
    const pairs: ManualMatchPair[] = [];
    const seen = new Set<string>();
    for (const s of unmatchSigs) {
      const rec = manualByLeg.get(s);
      if (!rec || seen.has(pairKey(rec))) continue;
      seen.add(pairKey(rec));
      // The STORED object where there is one, so the removal keys exactly as it was saved.
      pairs.push(sessionPairs.find((p) => pairKey(p) === pairKey(rec))
        ?? remembered.find((p) => pairKey(p) === pairKey(rec)) ?? rec);
    }
    // A prior-period pair made from a saved main-tab match whose rows rolled forward is
    // that match: it lives in the MAIN store, so Unmatch forgets it there (the carried
    // store never held it, and deleting from that one silently changed nothing).
    const rolledUndo = priorUndo.filter((s) => s.undo === "forget" && s.pair?.main_pair);
    const rolledKeys = new Set<string>();
    for (const s of rolledUndo) {
      const m = s.pair!.main_pair!;
      rolledKeys.add(pairKey(m));
      if (seen.has(pairKey(m))) continue;
      seen.add(pairKey(m));
      pairs.push(remembered.find((p) => pairKey(p) === pairKey(m)) ?? m);
    }
    priorUndo = priorUndo.filter((s) => !rolledUndo.includes(s));
    let unmatched: ManualMatchPair[] = [];
    if (pairs.length) {
      try {
        const r = await api.removeManualMatches(insurerName, pairs);
        setRemembered(r.pairs);
        unmatched = pairs;
      } catch (e) {
        errors.push("could not unmatch: " + String(e));
      }
    }
    // Both stores are read in the finalize tail, so the one re-finalize below applies them.
    let rejected = 0;
    let forgot = 0;
    for (const s of priorUndo) {
      try {
        if (s.undo === "reject") {
          await api.rejectUnreconciledClear(insurerName, s.sig, "", s.book);
          rejected++;
        } else if (s.undo === "forget" && s.pair) {
          await api.removeCarriedMatches(insurerName, [s.pair]);
          forgot++;
        }
      } catch {
        // counted below
      }
    }
    if (rejected + forgot < priorUndo.length) {
      errors.push(`could not undo ${priorUndo.length - rejected - forgot} of ${priorUndo.length} prior-period match${
        priorUndo.length === 1 ? "" : "es"}`);
    }
    if (forgot) setPriorStoreVersion((v) => v + 1);
    if (!sent.length && !unmatched.length && !rejected && !forgot) {
      setError("Nothing was changed: " + errors.join("; "));
      setBusy(false);
      return;
    }
    const before = sessionPairs;
    const gone = new Set(unmatched.map(pairKey));
    // busy stays TRUE through the re-finalize. Dropping it (and letting refinalize raise
    // it again) opens a window in which every button is live while the change is still
    // settling — a second click then acts on a pair the user never meant to touch.
    // FULL frames when an unmatched pair was stamped at upload, or a prior-period pair was
    // undone: the delta cannot revert either (it carries only rows still stamped). So
    // does a settled-before or Cleared release: the other leg's note changes on a row it
    // may omit.
    const sbReleased = sent.some((g) => g.some((s) => clearings.has(s) || isSettledBeforeMatch(
      s.startsWith("INS|") ? result.df_insurer : result.df_cri, s)));
    const r = await refinalize(before.filter((p) => !gone.has(pairKey(p))), null,
                               unmatched.some((p) => baselineApplied.has(pairKey(p)))
                               || unmatched.some((p) => rolledKeys.has(pairKey(p))) || sbReleased
                               || rejected + forgot > 0);
    if (!r) return;
    if (unmatched.length) setForgotten({ pairs: unmatched, sessionPairs: before });

    const inert = await dropInertReleases(r, sent, stored);
    const nRel = sent.length - inert.length;
    const parts = [
      nRel ? (named && sent.length === 1
        ? `Released ${named} and its counterpart — that one pair only`
        : `Released ${nRel} pair${nRel === 1 ? "" : "s"}`) : "",
      unmatched.length
        ? `unmatched ${unmatched.length} hand-made match${unmatched.length === 1 ? "" : "es"}`
        : "",
      rejected ? `held ${rejected} prior-period item${rejected === 1 ? "" : "s"} open` : "",
      forgot ? `forgot ${forgot} hand-made prior-period match${forgot === 1 ? "" : "es"}` : "",
    ].filter(Boolean);
    if (parts.length) {
      const msg = parts.join(" and ");
      // An item open again moves the Adjusted Closing, so "unchanged" is not claimed then.
      toast.success(`${msg[0].toUpperCase()}${msg.slice(1)}.${
        rejected + forgot ? "" : " Totals are unchanged."}`);
    }
    if (inert.length) {
      errors.push(`nothing was released for ${inert.length === 1 ? (named ?? "1 pair")
        : `${inert.length} pairs`}: ${inert[0].reason}`);
    }
    if (errors.length) setError(errors.map((e) => e[0].toUpperCase() + e.slice(1)).join(". ") + ".");
  }

  // A release that freed NOTHING (the row is held by a manual or prior-period match, or is
  // not in this upload) must not stay saved: it would be listed as "Released" while doing
  // nothing, and would silently un-match the row if the app ever paired it later. No
  // re-finalize — an entry that freed nothing changed no figure. A stored group sharing a
  // leg with a WORKING release is left alone, since the store's restore drops every group
  // overlapping the signatures sent.
  async function dropInertReleases(r: ReconResult, sent: string[][], stored: ReleasedGroup[]) {
    const why = new Map((r.released_unapplied ?? []).map((u) => [u.signature, u]));
    const inert = sent.filter((g) => g.length > 0 && g.every((s) => why.has(s)));
    const drop = inert.flat().filter((s) => stored.every(
      (g) => !g.sigs.includes(s) || g.sigs.every((x) => why.has(x))));
    if (drop.length) {
      try {
        setReleased((await api.restoreMatch(insurerName, drop)).released);
        setReleasedError("");
      } catch {
        // Left stored: the Released list flags it "Not applied" with the same reason.
      }
    }
    return inert.map((g) => why.get(g[0])!);
  }

  // Release one auto-match. NAME the pair: the panel can list several candidates, and an
  // unqualified "Match released" left the user unable to tell which one the click acted
  // on — the doubt that made a correct single release look like it had freed everything.
  async function releaseMatch(sig: string) {
    const isIns = sig.startsWith("INS|");
    const what = sigLabel(isIns ? result.df_insurer : result.df_cri, sig,
                          isIns ? result.display_config?.insurer : result.display_config?.cri);
    await undoMatches([[sig]], [], what);
  }

  const releaseAll = (groups: string[][], unmatchSigs: string[], priorUndo: PriorSettlement[]) =>
    undoMatches(groups, unmatchSigs, undefined, priorUndo);
  const unmatchOne = (sig: string) => undoMatches([], [sig]);
  const undoPrior = (s: PriorSettlement) => undoMatches([], [], undefined, [s]);
  const priorSettlement = useCallback((gid: string) => priorSettlementOf(prior, gid), [prior]);
  // The app's Cleared in Subsequent Month pairings, by leg: the search panel releases them.
  const clearings = useMemo(() => autoClearings(result), [result]);
  const clearedOf = useCallback((sig: string) => clearings.get(sig), [clearings]);

  async function restoreMatch(g: ReleasedGroup) {
    return restoreMatches([g]);
  }

  // One call for any number of groups: the store drops every group overlapping the sigs.
  async function restoreMatches(gs: ReleasedGroup[]) {
    if (!gs.length) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.restoreMatch(insurerName, gs.flatMap((g) => g.sigs));
      setReleased(r.released);
      setReleasedError("");
    } catch (e) {
      setError("Could not restore the match: " + String(e));
      setBusy(false);
      return;
    }
    // busy stays TRUE through the re-finalize, as in releaseMatch above.
    // FULL FRAMES, not the delta: the pair is matched again by whatever engine tier the
    // matcher picks, and the delta carries only Tier-6 + released rows — so the restored
    // row would be missing from it and the browser would keep its stale unmatched copy.
    await refinalize(sessionPairs, gs.length === 1
      ? "Match restored — the rows are matched again."
      : `${gs.length} matches restored — the rows are matched again.`, true);
  }

  // The per-insurer on-disk store (auto-applied on every upload of this insurer).
  //
  // The load state is tracked, NOT swallowed. This fetch previously ended in
  // `.catch(() => {})`, so a failed request left the list empty and the panel below
  // rendered its "None saved yet" copy — stating, with confidence, that nothing is
  // stored. That is the worst possible failure for this panel: a remembered pair keeps
  // being APPLIED by the server on every finalize whether or not the browser managed to
  // list it, so an unreachable store shows the user an empty panel while a stored
  // override is still moving their figures. (Real case: AL-ITTIHAD carried a same-book
  // pair of two positive CRI receipts that netted 30,531.71 into Figure/Balance
  // Mismatch every run, while this panel said "None saved yet" — leaving no way to find
  // or remove it.) An error must therefore read as an ERROR, never as an empty store.
  const [remembered, setRemembered] = useState<ManualMatchPair[]>([]);
  const [rememberedError, setRememberedError] = useState("");
  // What the last Forget removed, held so it can be put back (see restoreForgotten). The
  // session list is snapshotted ALONGSIDE the pairs: a remembered match that never
  // resolved this run was not in it, so restoring by appending to whatever the list holds
  // at undo time would re-send it as a session match (and mark it made this session).
  const [forgotten, setForgotten] = useState<
    { pairs: ManualMatchPair[]; sessionPairs: ManualMatchPair[] } | null>(null);
  useEffect(() => {
    let stale = false;
    // No insurer yet (first render before the result lands): not an error, and not a
    // reason to claim the store is empty — leave the panel in its loading state.
    if (!insurerName) return;
    setRememberedError("");
    // A snapshot belongs to ONE insurer's store; carrying it across would offer to write
    // another insurer's pairs into this one.
    setForgotten(null);
    api.manualMatches(insurerName)
      .then((r) => { if (!stale) setRemembered(r.pairs ?? []); })
      .catch((e) => {
        if (stale) return;
        setRemembered([]);
        setRememberedError(String(e));
      });
    return () => { stale = true; };
  }, [insurerName]);

  // Pairs the engine actually applied THIS run (session + remembered that resolved).
  const appliedSigs = useMemo(() => {
    const arr = (result.manual_matched as ManualMatchPair[] | undefined) ?? [];
    return new Set(arr.map(pairKey));
  }, [result]);
  // Pairs already stamped in the BASELINE frames (applied at upload, or at the last full
  // refresh). A delta can only ADD stamps on top of the baseline — it cannot revert a
  // stamped row, whose pre-match values the client never received — so removing one of
  // these must fetch full frames or its rows stay displayed as matched.
  const baselineApplied = useMemo(() => {
    const arr = (baseline.manual_matched as ManualMatchPair[] | undefined) ?? [];
    return new Set(arr.map(pairKey));
  }, [baseline]);

  // Legs the engine REFUSED this run, so an unapplied match can say WHY rather than just
  // "not in current files". The engine distinguishes "no matching unmatched row" from
  // "ambiguous — 2+ rows share this signature", and those need different actions from the
  // user (re-match it vs. the row is genuinely duplicated), so the reason is surfaced
  // verbatim on the match it belongs to.
  const unresolved = useMemo(
    () => (result.manual_unresolved as ManualUnresolved[] | undefined) ?? [],
    [result]);

  // THE ONE LIST of manual matches (finance 2026-09-26): the remembered store plus any
  // match made this session that the store does not hold (lib/savedMatches). It replaced
  // separate "Applied manual matches" and "Not applied this run" lists that repeated the
  // same matches; what only they showed (made this session, the engine's reason) is
  // carried on each entry, and a refusal naming no listed match is kept as an orphan.
  // Saved matches whose rows rolled forward applied to those prior-period items instead.
  const rolledSigs = useMemo(() => new Map(
    ((result.manual_rolled as ManualRolled[] | undefined) ?? [])
      .map((r): [string, boolean] => [pairKey(r.pair), r.settled])), [result]);
  const savedList = useMemo(
    () => buildSavedMatches(remembered, sessionPairs, appliedSigs, unresolved, rolledSigs),
    [remembered, sessionPairs, appliedSigs, unresolved, rolledSigs]);
  const nNotApplied = savedList.items.filter((i) => !i.applied).length;
  const nThisSession = savedList.items.filter((i) => i.thisSession).length;

  // The list split by what it DID this run. Only non-empty sections are rendered, so a
  // list where everything applied shows one section saying exactly that, rather than an
  // empty "not found" header implying a problem.
  const rememberedSections = useMemo(() => {
    const applied: SavedMatch[] = [];
    const open: SavedMatch[] = [];
    for (const it of savedList.items) (it.applied ? applied : open).push(it);
    const n = (k: number) => `${k} match${k === 1 ? "" : "es"}`;
    return [
      applied.length ? {
        key: "applied", applied: true, items: applied,
        title: "Applied this run", sub: n(applied.length),
        hint: "These saved matches resolved against the files you uploaded, and the "
            + "figures above already reflect them.",
      } : null,
      // "Not APPLIED", not "not found". Only one of the two reasons is a missing row:
      // an ambiguous pair's rows ARE in the files — the engine refuses to guess which of
      // several identical rows was meant. Titling the section after the rarer cause would
      // send finance looking for transactions that are sitting right there.
      open.length ? {
        key: "open", applied: false, items: open,
        title: "Not applied this run", sub: n(open.length),
        hint: "Either no row matched (an amount or reference changed), or two or more "
            + "rows now share one signature and the app will not guess which was meant. "
            + "Each row below says which. Nothing was matched for them and no figure "
            + "moved: every row involved stays in its open bucket. They all remain saved; "
            + "a missing or ambiguous one re-applies as soon as it resolves to a single "
            + "row, while one whose row changed must be ticked again.",
      } : null,
    ].filter((s): s is NonNullable<typeof s> => s !== null);
  }, [savedList]);

  // TABLE VIEW of the stored-match lists (finance 2026-09-24): default Table so a long
  // list can be filtered and footed like any ledger grid; the List stays one click away.
  // Entries carry their status so the table needs no sections.
  const [rememberedView, setRememberedView] = useState<"list" | "table">("table");
  const [releasedView, setReleasedView] = useState<"list" | "table">("table");
  const rememberedEntries = useMemo(() => savedList.items.map((it): MatchListEntry => {
    const { cri, ins } = groupSigs(it.pair);
    return {
      key: it.key, status: it.status, sigs: [...cri, ...ins], origin: it.origin,
      detail: it.reason ? `${it.reason.side}: ${it.reason.reason}` : undefined,
    };
  }), [savedList]);

  // SECTION DEFAULTS (finance 2026-09-26): the match grids open, the lists folded with
  // their counts in the header — but the saved list opens by itself whenever a match in it
  // did not apply (or the store could not be read). Such a match silently moves nothing
  // while the figures assume it did, so it must never start life hidden.
  const [matchOpen, setMatchOpen] = useState(true);
  const [savedOpen, setSavedOpen] = useState(false);
  const [releasedOpen, setReleasedOpen] = useState(false);
  const savedAttention = nNotApplied > 0 || savedList.orphans.length > 0 || rememberedError !== "";
  useEffect(() => { if (savedAttention) setSavedOpen(true); }, [savedAttention]);

  // THE MATCHING WORKSPACE (lib/useMatchWorkspace). Opened on purpose, never by a tick.
  const { workspace, setWorkspace, wsLayout, setWsLayout, wsRef, booksRef, gridHs,
          matchbarRef } = useMatchWorkspace();
  function openWorkspace() {
    setMatchOpen(true);
    setWorkspace(true);
  }
  const releasedKey = (g: ReleasedGroup) => g.sigs.join("⟷");
  // What each stored release did THIS run (backend _unapplied_releases). One whose every
  // leg freed nothing is inert and is said to be, never listed as "Released".
  const releasedWhy = useMemo(() => new Map(
    (result.released_unapplied ?? []).map((u) => [u.signature, u])), [result]);
  const releasedInert = (g: ReleasedGroup) => {
    const why = g.sigs.map((s) => releasedWhy.get(s));
    return why.every(Boolean) ? why[0] : undefined;
  };
  const INERT_STATUS: Record<string, string> = {
    manual: "Not applied: matched by hand",
    prior: "Not applied: prior-period match",
    none: "Not applied: no automatic match",
    ambiguous: "Not applied: ambiguous",
    rekeyed: "Not applied: row changed",
  };
  const releasedEntries = useMemo(
    () => released.map((g) => {
      const inert = releasedInert(g);
      return { key: releasedKey(g), sigs: g.sigs,
               status: inert ? INERT_STATUS[inert.kind] ?? "Not applied" : "Released" };
    }),
    [released, releasedWhy]);
  const nReleasedInert = releasedEntries.filter((e) => e.status !== "Released").length;
  const byKey = <T,>(items: T[], keyOf: (t: T) => string, keys: string[]) => {
    const want = new Set(keys);
    return items.filter((t) => want.has(keyOf(t)));
  };

  // The ticked rows, for the tray in the match bar. The grid highlights ticked rows, but a
  // pick can scroll out of view or sit behind a column filter, so the basket must stay
  // visible and removable in one place. It used to sit above each grid, which pushed that
  // book down as rows were ticked and knocked the two books out of line.
  // A prior-period item is named and priced off its own carried list.
  const priorLeg = (sig: string, b: Book, remove: (sig: string) => void) => ({
    sig, side: b === "cri" ? "Prior CRI" : "Prior insurer", frame: carriedRows(prior, b),
    sigCol: CARRIED_SIG, config: priorConfigs[b],
    amount: carriedAmountFor(carriedRows(prior, b), CARRIED_SIG, sig, CARRIED_AMOUNTS), remove,
  });
  const trayLegs: { sig: string; side: string; frame: FrameRecords; sigCol?: string;
                    config?: SideDisplayConfig; amount: number | null;
                    remove: (sig: string) => void }[] = [
    ...criPicks.map((sig) => isPriorSig(sig) ? priorLeg(sig, "cri", toggleCri) : ({
      sig, side: "CRI", frame: criUnmatched,
      config: result.display_config?.cri, amount: amountFor(criUnmatched, sig, "cri_amount"),
      remove: toggleCri })),
    ...insPicks.map((sig) => isPriorSig(sig) ? priorLeg(sig, "insurer", toggleIns) : ({
      sig, side: "Insurer", frame: insUnmatched,
      config: result.display_config?.insurer,
      amount: amountFor(insUnmatched, sig, "balance_amount"), remove: toggleIns })),
  ];

  // Every applied / remembered match is rendered by MatchGroupRow, against the FULL
  // frames: an applied match's rows are matched, so they are not in the unmatched slices
  // the grids above show.
  const matchRowProps = {
    criFrame: result.df_cri, insFrame: result.df_insurer,
    criConfig: result.display_config?.cri, insConfig: result.display_config?.insurer,
    insurerName, signRules: result.manual_match_rules,
  };
  const listTableFrames = {
    criFrame: result.df_cri, insFrame: result.df_insurer,
    criConfig: result.display_config?.cri, insConfig: result.display_config?.insurer,
    insurerName, signRules: result.manual_match_rules,
  };

  // `fullFrames` forces the complete payload instead of the delta. Needed when a change
  // can re-match rows through ARBITRARY ENGINE TIERS, which the delta cannot enumerate:
  // it carries Tier-6 manual rows plus explicitly-named released rows, and nothing else.
  // A RESTORE is exactly that case — the pair matches automatically again at whatever
  // tier the matcher chooses (tier 2 on Tokio), so it appears in neither list, and the
  // browser would keep the STALE UNMATCHED copy it wrote when the row was released.
  // That is the reported bug: after Restore the row could not be found under matched.
  // Returns the new result (null on failure) for a caller that must read it; a null
  // successMsg leaves the toast to that caller.
  async function refinalize(pairs: ManualMatchPair[], successMsg: string | null,
                            fullFrames = false): Promise<ReconResult | null> {
    setBusy(true);
    setError("");
    // Any change to the figures invalidates a pending undo: its snapshot describes the
    // session list as it was BEFORE, so restoring it afterwards would silently revert
    // whatever the user did in between. forgetPairs re-arms it once its own call returns.
    setForgotten(null);
    try {
      // Ask for the FRAME DELTA: a match changes only the rows it pairs, so the server
      // recomputes every figure but returns just those rows. On Dubai that's ~0.5MB
      // instead of ~75MB — the difference between a 10s apply and an instant one.
      // A FULL response re-establishes the baseline (third argument). Keeping the old one
      // is what went stale: every later delta was patched onto rows as they stood at
      // upload, so a restored pair flipped back to unmatched on the next match.
      if (fullFrames) {
        const full = await api.finalize({ prematchId, insurerName, sessionPairs: pairs });
        onApplied(full.result, pairs, true);
        if (successMsg) toast.success(successMsg);
        return full.result;
      }
      const resp = await api.finalize({ prematchId, insurerName, sessionPairs: pairs, framesDelta: true });
      let next = applyFramesDelta(resp.result, baseline);
      let isFull = false;
      if (!next) {
        // The held frames didn't line up with the delta (or the server sent full
        // frames anyway). Refetch the complete payload so the grids are never built
        // from a frame we can't prove is correct — slower, but always right.
        const full = await api.finalize({ prematchId, insurerName, sessionPairs: pairs });
        next = full.result;
        isFull = true;
      }
      onApplied(next, pairs, isFull);
      if (successMsg) toast.success(successMsg);
      return next;
    } catch (e) {
      // The server's saved session expired (409). Re-run once, then retry on the FULL
      // path: the baseline in this closure belongs to the expired run, so a delta patched
      // onto it could not be trusted.
      if (e instanceof ApiError && e.status === 409 && onPrematchExpired) {
        try {
          toast.info("Session data had expired — re-running the reconciliation, then applying your change…");
          const pid = await onPrematchExpired();
          const full = await api.finalize({ prematchId: pid, insurerName, sessionPairs: pairs });
          onApplied(full.result, pairs, true);
          if (successMsg) toast.success(successMsg);
          return full.result;
        } catch (e2) {
          e = e2;
        }
      }
      setError("Could not apply: " + (e instanceof Error ? e.message : String(e)));
      return null;
    } finally {
      setBusy(false);
    }
  }

  // Build a match from any set of picked signatures, regardless of which grid each came
  // from. The engine's signatures are self-identifying (CRI rows start "CRI|", insurer
  // rows "INS|"), so a crossed selection — the user ticks a CRI row in the insurer grid
  // — still lands the right way round.
  //
  // A 1:1 selection is sent in the classic {cri_sig, insurer_sig} shape so it keys and
  // dedupes identically to every pair stored before groups existed; anything else goes
  // as the general {cri_sigs, insurer_sigs} group. Returns null for fewer than two
  // distinct rows, which has nothing to settle against.
  function buildGroup(sigs: string[]): ManualMatchPair | null {
    const seen = new Set<string>();
    const cri: string[] = [];
    const ins: string[] = [];
    for (const s of sigs) {
      if (!s || seen.has(s)) continue;
      seen.add(s);
      (s.startsWith("INS|") ? ins : cri).push(s);
    }
    if (cri.length + ins.length < 2) return null;
    if (cri.length === 1 && ins.length === 1) return { cri_sig: cri[0], insurer_sig: ins[0] };
    return { cri_sigs: cri, insurer_sigs: ins };
  }

  // Apply one or more pairs: persist each to the per-insurer store FIRST (parity with
  // the Streamlit add_pair-on-click — the store is what auto-applies on the next
  // upload), then re-finalize ONCE for instant totals.
  //
  // The single re-finalize is the point of batching: it's the whole cost of an apply
  // (~2.5s on Dubai), and it's flat in the number of pairs, so N pairs cost one round
  // trip instead of N. Store writes are small local-disk calls, issued together.
  async function applyPairs(pairs: ManualMatchPair[]): Promise<boolean> {
    // Dedupe on the pair's identity key so BOTH kinds compare correctly (a same-book
    // pair has no cri_sig/insurer_sig, so a field-wise compare would call every
    // same-book pair a duplicate of every other).
    const already = new Set(sessionPairs.map(pairKey));
    let fresh = pairs.filter((pair) => !already.has(pairKey(pair)));
    if (!fresh.length) return false;
    // Ask before saving, only when it applies: an older saved match claiming a row, or a
    // ticked row with look-alikes. Cancel saves nothing.
    const answers = await askBeforeSave(fresh);
    if (!answers) return false;
    fresh = fresh.map((p, i) => (Object.keys(answers.pins[i] ?? {}).length ? { ...p, pins: answers.pins[i] } : p));
    const replaced = new Set(answers.replace.flat().map(pairKey));
    // Persist before finalizing. A failed store write is NOT fatal: the pair still
    // applies this session, so we warn and finalize the full set anyway — dropping it
    // here would leave the displayed totals disagreeing with what was actually matched.
    const results = await Promise.allSettled(
      fresh.map((p, i) => api.addManualMatch(insurerName, p, answers.replace[i] ?? [])));
    const saved = results.filter((r) => r.status === "fulfilled");
    if (saved.length) {
      // Each response echoes the full store; the last successful one is the newest.
      setRemembered((saved[saved.length - 1] as PromiseFulfilledResult<{ pairs: ManualMatchPair[] }>).value.pairs);
    }
    const failed = results.length - saved.length;
    if (failed) {
      // The server says why (a store it cannot read or a failed write), so finance can report it.
      const why = (results.find((r) => r.status === "rejected") as PromiseRejectedResult).reason;
      toast.error((failed === 1
        ? "1 pair applied for this session, but could not be saved for future uploads."
        : `${failed} pairs applied for this session, but could not be saved for future uploads.`)
        + ` ${why instanceof Error ? why.message : String(why)}`);
    }
    // Name what was actually applied: a lone group reports its row count (a 4-row
    // many-to-one reading "1 pair matched" would understate what just moved).
    const legs = (p: ManualMatchPair) => {
      const { cri, ins } = groupSigs(p);
      return cri.length + ins.length;
    };
    const msg = fresh.length > 1
      ? `${fresh.length} matches applied — totals updated.`
      : isSameBookPair(fresh[0])
        // An offset never crossed the books, so it must not read back as a "pair matched".
        ? `${legs(fresh[0])} rows offset within one ledger — totals updated.`
        : legs(fresh[0]) > 2
          ? `${legs(fresh[0])} rows matched as one group — totals updated.`
          : "Pair matched — totals updated.";
    // A replaced match made earlier this session must stop applying now, not next upload.
    return (await refinalize([...sessionPairs.filter((p) => !replaced.has(pairKey(p))), ...fresh], msg)) !== null;
  }

  // The save dialog's open state; `resolve` settles askBeforeSave's promise.
  const [saveAsk, setSaveAsk] = useState<{
    pairs: ManualMatchPair[]; checks: SaveCheck[]; resolve: (a: SaveAnswers | null) => void;
  } | null>(null);

  // Resolves to finance's answers (empty when nothing needed asking) or null on Cancel.
  // A check that fails saves as before: it must never stand between finance and a match.
  async function askBeforeSave(fresh: ManualMatchPair[]): Promise<SaveAnswers | null> {
    const none: SaveAnswers = { pins: fresh.map(() => ({})), replace: fresh.map(() => []) };
    let checks: SaveCheck[];
    try {
      checks = (await api.precheckManualMatches(prematchId, insurerName, fresh)).checks;
    } catch {
      return none;
    }
    if (!checks.some((c) => c.conflicts.length || c.lookalikes.length)) return none;
    return new Promise((resolve) => setSaveAsk({ pairs: fresh, checks, resolve }));
  }

  const applyPair = (pair: ManualMatchPair) => applyPairs([pair]);

  // Bumped after a prior-period pair is saved, so the prior-period list reads the store back
  // and drops a pending Forget undo (the store it describes just changed).
  const [priorStoreVersion, setPriorStoreVersion] = useState(0);

  // A prior-period pair goes to its OWN store (the carried resolver applies it in the
  // finalize tail, never _apply_manual_matches), then the reconciliation re-runs.
  async function savePriorPair(pair: CarriedMatchPair, book: Book, successMsg: string) {
    setBusy(true);
    setError("");
    try {
      // Stamp the list on every pair leaving the bar: the store keys on it.
      await api.addCarriedMatch(insurerName, { ...pair, book });
      setPriorStoreVersion((v) => v + 1);
      toast.success(successMsg);
      // busy stays TRUE through the re-run, as in undoMatches.
      await onCarriedPairsChanged?.();
      return true;
    } catch (e) {
      setError("Could not save the match: " + String(e));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function matchPair() {
    if (pv) {
      if (!pv.pair || pv.refusal) return;
      const ok = await savePriorPair(pv.pair, pv.book, pv.kind === "offset"
        ? `${pv.nPrior} prior-period items offset — recalculating.`
        : "Match saved — recalculating.");
      if (ok) clearPicks();
      return;
    }
    const pair = buildGroup([...criPicks, ...insPicks]);
    if (!pair) {
      setError("Pick at least two different rows.");
      return;
    }
    // A within-ledger offset moves real money between buckets, so the user is told so
    // BEFORE clicking — the basket labels it and warns while the rows are being ticked,
    // rather than swallowing a first click to ask. One click applies either kind.
    const ok = await applyPair(pair);
    if (ok) clearPicks();
  }

  async function acceptSuggestion(s: NearMiss): Promise<boolean> {
    // near-miss sigs are already correctly sided, but normalise defensively.
    const pair = buildGroup([s.cri_sig, s.insurer_sig]);
    if (!pair) return false;
    return applyPair(pair);
  }

  // The ticked set split into what can all be applied together and what competes for a
  // shared row. Recomputed as the selection changes so the Accept button can say up front
  // how many will actually land.
  const split = useMemo(() => splitContested(selectedForAction), [selectedForAction]);

  // Is the rival-review panel expanded? The choice is offered BEFORE accepting, which is
  // the only moment it can be: once a winner is matched its row is consumed, so the
  // rebuilt queue correctly drops every rival and there is nothing left to choose between.
  const [reviewOpen, setReviewOpen] = useState(false);

  // Accept every ticked suggestion in a single re-finalize.
  //
  // ONLY the uncontested subset is sent. The engine consumes a leg as soon as a group
  // takes it (_apply_manual_matches: `ins_map[sig] = None`) and refuses every later group
  // naming that row — correctly, since matching one insurer row against three CRI rows
  // would double-count the money. Sending the whole set anyway "worked" but applied fewer
  // matches than the button claimed, and explained the shortfall only in the unresolved
  // panel further down the page (reported live on Salama: 32 ticked, 29 applied, 3
  // "no matching unmatched Insurer row").
  //
  // The rivals left out are NOT re-offered afterwards: accepting a winner consumes the
  // row, so they stop being near-misses at all. Their moment is before the click — hence
  // the review panel above, which is live while every rival still exists.
  async function acceptSelected() {
    const pairs = split.ok
      .map((s) => buildGroup([s.cri_sig, s.insurer_sig]))
      .filter((p): p is ManualMatchPair => p !== null);
    if (!pairs.length) return;
    const applied = await applyPairs(pairs);
    if (applied) {
      setSelected(new Set());
      setReviewOpen(false);
    }
  }

  // Dismiss pairs from view. SESSION-ONLY and figure-neutral: nothing is matched, nothing
  // is persisted, no row leaves its open bucket — a reload brings them all back. That is
  // what makes a bulk reject safe to offer over a filtered set.
  function rejectSuggestions(list: NearMiss[]) {
    if (!list.length) return;
    const keys = list.map(pairKeyOf);
    setDismissed((prev) => {
      const next = new Set(prev);
      for (const k of keys) next.add(k);
      return next;
    });
    // Drop them from the batch selection too, so the "Accept selected" count can never
    // include a pair that's no longer on screen.
    setSelected((prev) => {
      const next = new Set(prev);
      let hit = false;
      for (const k of keys) if (next.delete(k)) hit = true;
      return hit ? next : prev;
    });
    toast.success(list.length === 1
      ? "Suggestion hidden for this session — no figure changed."
      : `${list.length} suggestions hidden for this session — no figure changed.`);
  }

  const rejectSuggestion = (s: NearMiss) => rejectSuggestions([s]);

  // Forget a remembered (on-disk) pair; re-finalize so a pair applied this run is
  // released immediately, not just on the next upload.
  async function forgetPairs(pairs: ManualMatchPair[]) {
    if (!pairs.length) return;
    // Taken BEFORE anything moves — this is what an undo restores to.
    const before = sessionPairs;
    try {
      const r = await api.removeManualMatches(insurerName, pairs);
      setRemembered(r.pairs);
    } catch (e) {
      setError("Could not update saved matches: " + String(e));
      return;
    }
    const gone = new Set(pairs.map(pairKey));
    const ok = await refinalize(
      sessionPairs.filter((x) => !gone.has(pairKey(x))),
      pairs.length === 1 ? "Saved match forgotten." : `${pairs.length} saved matches forgotten.`,
      pairs.some((p) => baselineApplied.has(pairKey(p)))
    );
    // AFTER the re-finalize, which clears any earlier snapshot. Only offered once the
    // change actually landed: an undo for a forget that failed would be a lie.
    if (ok) setForgotten({ pairs, sessionPairs: before });
  }

  // Put back exactly what the last Forget removed: the store entries, and the session
  // list as it stood before. Re-uses the same add-then-finalize path a manual match takes,
  // so a restored pair is indistinguishable from one that was never forgotten.
  async function restoreForgotten(snap: { pairs: ManualMatchPair[]; sessionPairs: ManualMatchPair[] }) {
    setBusy(true);
    setError("");
    const results = await Promise.allSettled(
      snap.pairs.map((p) => api.addManualMatch(insurerName, p)));
    const saved = results.filter((r) => r.status === "fulfilled");
    if (saved.length) {
      // Each response echoes the full store; the last successful one is the newest.
      setRemembered((saved[saved.length - 1] as PromiseFulfilledResult<{ pairs: ManualMatchPair[] }>).value.pairs);
    }
    // The ones that did NOT make it back — the banner keeps offering exactly those, so a
    // retry can never re-add a pair that is already restored or overstate what is missing.
    const stillGone = snap.pairs.filter((_, i) => results[i].status === "rejected");
    if (stillGone.length) {
      // A PARTIAL restore is stated, never rounded to success or to failure: some pairs
      // are back and some are still gone, and only the user can act on the difference.
      toast.error(stillGone.length === 1
        ? "1 match could not be restored. The rest are back — it is still offered below, so you can try again."
        : `${stillGone.length} matches could not be restored. The rest are back — they are still offered below, so you can try again.`);
    }
    if (!saved.length) {
      // Nothing reached the store, so nothing to re-finalize and the offer stands as it is.
      setBusy(false);
      return;
    }
    await refinalize(snap.sessionPairs,
      saved.length === 1 ? "Saved match restored." : `${saved.length} saved matches restored.`);
    if (stillGone.length) setForgotten({ pairs: stillGone, sessionPairs: snap.sessionPairs });
  }

  return (
    <div>
      <p className="muted mm-intro">
        Tick the rows that settle each other, in either book or both, then Match. Paste a column
        of references from Excel into a search box to find them all at once.
        <InfoTip text="One CRI row against one Insurer row is the usual case, but you can tick several on either side, or both: three invoices against one receipt, or two rows within the same ledger that cancel out. Use the per-column filters to narrow by type or category. Applied matches update the totals instantly. Everything you tick becomes ONE match. If the two sides tie within your Matched tolerance the whole group is marked Matched; if they don't, every row in it is kept as a visible Figure/Balance Mismatch so the shortfall stays on the report. Either way your closing balances and the Difference are unchanged — the amounts only move between lines that are already netted the same way. Ticking two or more rows in a single grid (with nothing on the other side) offsets them against each other, e.g. an invoice and its own reversal." />
        {(criPriorN > 0 || insPriorN > 0) && (
          <>
            {" "}Items carried from earlier periods that are still open sit in the same books,
            marked <strong>Prior period</strong> in the Source column.
            <InfoTip text="Tick a prior-period item with the transaction(s) from this period that settled it, one item on each prior-period list (the two halves of one break), or two or more items on one list that cancel each other. A prior-period item is not a row of this period's ledger, so a match with one moves only the Adjusted Closing Balance — the Closing Balance and the Difference never change — and it must tie: a pair whose amounts do not agree settles nothing, and the item stays open, annotated with the gap. Saving one re-runs the reconciliation." />
          </>
        )}
        {result.segment_label && (
          <>
            {" "}Only {result.segment_label} rows are listed. To match a row here with one
            from the other view, switch to All business.
          </>
        )}
      </p>
      {error && <div className="badge badge-risk" style={{ display: "block", marginBottom: 8 }}>{error}</div>}

      {/* Suggested matches — engine near-misses (same reference/policy key + reconciling
          sign, left open only on the amount). One-click Accept routes through the same
          match flow; Reject dismisses for this session. The two free-form grids below
          still let you match ANY two rows the queue didn't propose. Folded by default; the
          count stays in the header so folding it loses no at-a-glance signal. */}
      {suggestions.length > 0 && (
        <CollapsibleSection title="Suggested matches" className="mm-section"
          badge={`${suggestions.length} to review`} badgeTone="in"
          open={suggestOpen} onOpenChange={setSuggestOpen}
          actions={<>
            <InfoTip text="Pairs that share a reference or policy number and reconcile in sign, but were left open because the amounts differ by more than your Matched tolerance. Accept to match a pair (it's remembered for future uploads); Reject to hide it for this session. Anything not listed here you can still match by hand in the two grids below." />
            {/* VIEW SWITCH. Same queue, same ticks — only the rendering changes, so a
                selection built in one view is still there in the other. */}
            {suggestOpen && (
              <span style={{ display: "inline-flex", gap: 4 }}>
                {(["list", "table"] as const).map((v) => (
                  <button key={v}
                    className={suggestView === v ? "btn btn-primary" : "btn"}
                    style={{ fontSize: "0.75rem", padding: "4px 10px", textTransform: "capitalize" }}
                    title={v === "table"
                      ? "Every suggestion in one filterable table, with every column from both ledgers — filter to exactly the ones you want, then select them all at once."
                      : "The compact queue, closest gap first."}
                    onClick={() => setSuggestView(v)}>
                    {v}
                  </button>
                ))}
              </span>
            )}
          </>}>
            <>
              <p className="muted" style={{ margin: "0 0 10px", fontSize: "0.8rem" }}>
                {suggestView === "table"
                  ? "Every suggestion, with every column from both ledgers. Filter or paste a column of references to narrow to exactly the pairs you want, then select them all in one click and accept them together."
                  : "Same reference or policy, amounts just outside tolerance — most likely the same document. Closest first."}
              </p>
              {/* Batch bar: tick several pairs and apply them in ONE round trip. Accepting
                  one at a time re-finalizes per pair, and the re-finalize is the entire
                  cost of an apply, so batching is what makes many matches cheap. */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                {/* Select-all. In LIST view it means the revealed window (all the user can
                    see); in TABLE view it means the whole queue — the grid's own button
                    covers the filtered subset, but it only appears once a filter or search
                    is active, so an unfiltered "take everything" needs to live here. */}
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.82rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    disabled={busy}
                    checked={allSelectableSelected}
                    ref={(el) => {
                      // Partial tick when only some of them are selected.
                      if (el) el.indeterminate = selectedForAction.length > 0 && !allSelectableSelected;
                    }}
                    onChange={() =>
                      selectManySuggestions(selectableSuggestions.map(pairKeyOf),
                                            !allSelectableSelected)}
                  />
                  {suggestView === "table" && !suggestFiltered
                    ? `Select all ${suggestions.length}`
                    : suggestView === "table"
                      ? `Select all ${tableShown.length} shown`
                      : "Select all shown"}
                </label>
                {/* The count is what will ACTUALLY be matched, not what is ticked. When
                    some ticked pairs compete for the same row the engine can only take
                    one of each, so promising the ticked number would overstate the
                    outcome — the exact way a bulk accept silently applied fewer. */}
                <button className="btn btn-primary" disabled={busy || !split.ok.length} onClick={acceptSelected}>
                  {busy ? "Applying…" : `Accept selected (${split.ok.length})`}
                </button>
                {/* Bulk dismiss. Session-only and figure-neutral (see rejectSuggestions),
                    so it is safe to offer over a filtered set — but it is NOT primary, so
                    it stays a plain button beside the accept. */}
                <button className="btn" disabled={busy || !selectedForAction.length}
                  title="Hide these suggestions for this session. Nothing is matched and no figure changes — they come back on reload."
                  onClick={() => rejectSuggestions(selectedForAction)}>
                  Reject selected ({selectedForAction.length})
                </button>
                {selectedForAction.length > 0 && (
                  <button className="btn" disabled={busy} onClick={() => setSelected(new Set())}>
                    Clear
                  </button>
                )}
                {selectedForAction.length > 1 && (
                  <span className="muted" style={{ fontSize: "0.78rem" }}>
                    Applied together in one update.
                  </span>
                )}
                {hiddenTickedSuggestions > 0 && (
                  <span className="muted" style={{ fontSize: "0.78rem" }}>
                    {hiddenTickedSuggestions} ticked suggestion
                    {hiddenTickedSuggestions === 1 ? " is" : "s are"} hidden by the search or
                    filters and left alone.
                  </span>
                )}
              </div>

              {/* CONTESTED ROWS, surfaced BEFORE the click. A bulk accept can never
                  quietly land fewer matches than its button promised, and the choice is
                  offered while every rival still exists — after an accept the winner's row
                  is consumed and the rivals stop being suggestions at all. */}
              {split.contested.length > 0 && (
                <div className="card" style={{ padding: 12, marginBottom: 12, borderLeft: "4px solid var(--warn, var(--risk))" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className="badge badge-warn">
                      {selectedForAction.length - split.ok.length} of {selectedForAction.length} can't be applied together
                    </span>
                    <span className="muted" style={{ fontSize: "0.79rem", flex: 1, minWidth: 220 }}>
                      Some ticked pairs want the same ledger row. One row can only belong to one
                      match, so <strong>{split.ok.length}</strong> will be applied. No figure moves either way.
                    </span>
                    <InfoTip text="A row can only be matched once — matching it twice would count the same money twice — so when several suggestions want the same row the app applies one and leaves the rest open. It will not pick for you: which one is genuinely the same document is a judgement about the paperwork. Review them here and accept the right one; the others stay open for manual matching or rejection." />
                    <button className="btn" style={{ fontSize: "0.75rem", padding: "4px 10px" }}
                      onClick={() => setReviewOpen((v) => !v)}>
                      {reviewOpen ? "Hide" : "Review"} {split.contested.length} contested row
                      {split.contested.length === 1 ? "" : "s"}
                    </button>
                  </div>

                  {/* Rivals for one row, side by side with their gaps, so the decision is
                      made on the documents rather than on which happened to sort first. */}
                  {reviewOpen && split.contested.map((g) => (
                    <div key={g.key} style={{ marginTop: 10 }}>
                      <div className="muted" style={{ fontSize: "0.76rem", marginBottom: 4 }}>
                        {g.rivals.length} suggestions want this {g.side} row:{" "}
                        <strong>
                          {sigLabel(g.side === "CRI" ? criUnmatched : insUnmatched, g.key,
                            g.side === "CRI" ? result.display_config?.cri : result.display_config?.insurer)}
                        </strong>
                      </div>
                      {g.rivals.map((s, i) => (
                        <div key={pairKeyOf(s)} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, marginBottom: 4, fontSize: "0.78rem", flexWrap: "wrap" }}>
                          {/* The first rival is the one a bulk Accept would take. */}
                          <span className={i === 0 ? "badge badge-success" : "badge"}>
                            {i === 0 ? "would be applied" : `alternative ${i}`}
                          </span>
                          <span style={{ flex: 1, minWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {sigLabel(criUnmatched, s.cri_sig, result.display_config?.cri)}
                            {" ⟷ "}
                            {sigLabel(insUnmatched, s.insurer_sig, result.display_config?.insurer)}
                          </span>
                          <span className="muted" style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                            {fmtAed(s.cri_amount)} ⟷ {fmtAed(s.insurer_amount)} · gap {fmtAed(s.amount_gap)}
                          </span>
                          <span style={{ display: "flex", gap: 6, whiteSpace: "nowrap" }}>
                            {/* Accept just this one: it applies alone, so the user can pick a
                                rival the bulk accept would not have taken. */}
                            <button className="btn btn-primary" disabled={busy}
                              onClick={() => acceptSuggestion(s)}>
                              Accept this one
                            </button>
                            {/* Drop a rival from the running so the bulk Accept can take the
                                one the user actually wants. */}
                            <button className="btn" disabled={busy}
                              onClick={() => rejectSuggestions([s])}>
                              Reject
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {suggestView === "table" ? (
                <ColumnFilterGrid
                  frame={suggestionGrid.frame} sigColumn="_sig"
                  selectedSig={null} selectedSigs={selected}
                  onSelect={(k) => k && toggleSelected(k)}
                  onSelectMany={selectManySuggestions}
                  onFilteredChange={setSuggestFiltered}
                  grouped insurerName={insurerName} compact
                  config={suggestionGrid.config} resetKey={`${gridResetKey}|suggestions`}
                  title="Suggested matches" />
              ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {suggestions.slice(0, suggestShown).map((s, i) => {
                  const criL = sigLabel(criUnmatched, s.cri_sig, result.display_config?.cri);
                  const insL = sigLabel(insUnmatched, s.insurer_sig, result.display_config?.insurer);
                  const willMismatch = s.amount_gap > 0.005;
                  const key = `${s.cri_sig}⟷${s.insurer_sig}`;
                  const isOpen = expanded.has(key);
                  return (
                    <div key={i} style={{ border: "1px solid var(--border)", borderRadius: 6, fontSize: "0.82rem" }}>
                      {/* Click the summary row to expand full transaction detail; Accept/Reject
                          stop propagation so acting on a pair never toggles it. */}
                      <div
                        onClick={() => toggleExpanded(key)}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", flexWrap: "wrap", cursor: "pointer" }}
                      >
                        {/* stopPropagation so ticking a pair doesn't also expand it */}
                        <span onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
                          <input
                            type="checkbox"
                            disabled={busy}
                            checked={selected.has(key)}
                            onChange={() => toggleSelected(key)}
                            aria-label="Select this suggested match"
                          />
                        </span>
                        <span style={{ color: "var(--text-muted)" }}>{isOpen ? "▾" : "▸"}</span>
                        <span className="badge" style={{ textTransform: "capitalize" }}>{s.reason}</span>
                        <span style={{ flex: 1, minWidth: 220, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{criL}</span>
                          <span className="muted">⟷</span>
                          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{insL}</span>
                        </span>
                        <span className="muted" style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {fmtAed(s.cri_amount)} ⟷ {fmtAed(s.insurer_amount)}
                          <span style={{ color: willMismatch ? "var(--risk)" : "var(--success)", marginLeft: 8 }}>
                            gap {fmtAed(s.amount_gap)}
                          </span>
                        </span>
                        <span onClick={(e) => e.stopPropagation()} style={{ display: "flex", gap: 6, whiteSpace: "nowrap" }}>
                          <button className="btn btn-primary" disabled={busy} onClick={() => acceptSuggestion(s)}>Accept</button>
                          <button className="btn" disabled={busy} onClick={() => rejectSuggestion(s)}>Reject</button>
                        </span>
                      </div>
                      {isOpen && (
                        <div style={{ padding: "8px 10px 12px 28px", borderTop: "1px solid var(--border)" }}>
                          <MatchDetailTable
                            criFrame={criUnmatched} criSigs={[s.cri_sig]} criConfig={result.display_config?.cri}
                            insFrame={insUnmatched} insSigs={[s.insurer_sig]} insConfig={result.display_config?.insurer}
                            insurerName={insurerName} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              )}
              {/* The reveal window is a LIST affordance only — the table already shows the
                  whole queue and pages it. */}
              {suggestView === "list" && suggestions.length > suggestShown && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                  <button className="btn" disabled={busy}
                    onClick={() => setSuggestShown((n) => n + SUGGEST_BATCH)}>
                    Show {Math.min(SUGGEST_BATCH, suggestions.length - suggestShown)} more
                  </button>
                  <span className="muted" style={{ fontSize: "0.78rem" }}>
                    Showing {suggestShown} of {suggestions.length}
                  </span>
                  <button className="btn" onClick={() => setSuggestView("table")}>
                    See all {suggestions.length} in a table
                  </button>
                </div>
              )}
            </>
        </CollapsibleSection>
      )}

      {/* MATCH TRANSACTIONS: both books and the docked match bar. Kept mounted while
          folded, so a search or filter survives a fold; the workspace toggle only restyles
          this one tree (never remounts it), for the same reason. */}
      <CollapsibleSection title="Match transactions" className="mm-section mm-match-section"
        keepMounted open={matchOpen}
        onOpenChange={(o) => { setMatchOpen(o); if (!o) setWorkspace(false); }}
        sub={`CRI ${criShown.total_rows.toLocaleString()} · Insurer ${
          insShown.total_rows.toLocaleString()} unmatched${criPriorN || insPriorN
            ? ` · prior period CRI ${criPriorN.toLocaleString()} · Insurer ${insPriorN.toLocaleString()} open`
            : ""}`}
        actions={
          <button className="btn btn-sm" onClick={openWorkspace}
            title="Both books and the match bar over the whole screen. Esc returns here.">
            Open matching workspace
          </button>
        }>
        <div ref={wsRef} className={workspace ? "mm-workspace" : "mm-workspace-host"}
          tabIndex={workspace ? -1 : undefined}
          role={workspace ? "dialog" : undefined} aria-modal={workspace ? true : undefined}
          aria-label={workspace ? `Matching workspace, ${insurerName}` : undefined}>
          {workspace && (
            <div className="mm-workspace-head">
              <span className="fullscreen-title">Matching workspace · {insurerName}</span>
              <span className="mm-seg" role="group" aria-label="Arrange the two books">
                {(["side", "stacked"] as const).map((v) => (
                  <button key={v} type="button" aria-pressed={wsLayout === v}
                    className={wsLayout === v ? "btn btn-sm btn-primary" : "btn btn-sm"}
                    title={v === "side"
                      ? "Books side by side, to compare row against row."
                      : "CRI above the insurer, each at full width, for less sideways scrolling."}
                    onClick={() => setWsLayout(v)}>
                    {v === "side" ? "Side by side" : "Stacked"}
                  </button>
                ))}
              </span>
              <button type="button" className="btn btn-sm" title="Exit the workspace (Esc)"
                onClick={() => setWorkspace(false)}>
                Exit
              </button>
            </div>
          )}

          {/* Both grids are always visible and both are multi-select: the kind of match is
              inferred from what ends up ticked, so neither side can be the "wrong" grid to
              click in. Ticked rows are echoed in the match bar's tray, not above a grid, so
              ticking never shifts one book against the other. */}
          <div ref={booksRef}
            className={"mm-books" + (workspace && wsLayout === "stacked" ? " mm-books--stacked" : "")}>
            <div className="mm-book">
              {criBook.total_rows === 0 ? (
                <p className="muted">No unmatched CRI rows — every CRI item is already matched or otherwise accounted for.</p>
              ) : (
                <ColumnFilterGrid frame={criBook} sigColumn="_sig"
                  selectedSig={null} selectedSigs={criPickSet} onSelect={toggleCri}
                  onSelectMany={selectManyCri}
                  fullFrame={result.df_cri} onRelease={releaseMatch} onReleaseAll={releaseAll}
                  manualGroupOf={manualGroupOf} onUnmatch={unmatchOne}
                  priorSettlementOf={priorSettlement} onUndoPrior={undoPrior}
                  clearedOf={clearedOf}
                  releaseBusy={busy}
                  otherFrame={result.df_insurer} otherConfig={result.display_config?.insurer}
                  config={criBookConfig} resetKey={gridResetKey}
                  compact maxHeight={gridHs[0]}
                  heading={<>CRI — unmatched ({criShown.total_rows}){criPriorN > 0 && <> · prior period ({criPriorN})</>}{reprintToggle(criReprintN)}</>}
                  pinSide="cri" insurerName={insurerName} title="CRI - unmatched"
                  ledgerView amountColumn="cri_amount"
                  amountFind={amountFind?.side === "cri" ? amountFind.target : null}
                  onClearAmountFind={() => setAmountFind(null)}
                  policyFind={policyFind?.side === "cri" ? policyFind.target : null}
                  onClearPolicyFind={() => setPolicyFind(null)} />
              )}
            </div>
            <div className="mm-book">
              {insBook.total_rows === 0 ? (
                <p className="muted">No unmatched Insurer rows — every insurer item is already matched or otherwise accounted for.</p>
              ) : (
                <ColumnFilterGrid frame={insBook} sigColumn="_sig"
                  selectedSig={null} selectedSigs={insPickSet} onSelect={toggleIns}
                  onSelectMany={selectManyIns}
                  fullFrame={result.df_insurer} onRelease={releaseMatch} onReleaseAll={releaseAll}
                  manualGroupOf={manualGroupOf} onUnmatch={unmatchOne}
                  priorSettlementOf={priorSettlement} onUndoPrior={undoPrior}
                  clearedOf={clearedOf}
                  releaseBusy={busy}
                  otherFrame={result.df_cri} otherConfig={result.display_config?.cri}
                  config={insBookConfig} resetKey={gridResetKey}
                  compact maxHeight={gridHs[1]}
                  heading={<>Insurer — unmatched ({insShown.total_rows}){insPriorN > 0 && <> · prior period ({insPriorN})</>}{reprintToggle(insReprintN)}</>}
                  pinSide="insurer" insurerName={insurerName} title="Insurer - unmatched"
                  ledgerView amountColumn="balance_amount"
                  amountFind={amountFind?.side === "insurer" ? amountFind.target : null}
                  onClearAmountFind={() => setAmountFind(null)}
                  policyFind={policyFind?.side === "insurer" ? policyFind.target : null}
                  onClearPolicyFind={() => setPolicyFind(null)} />
              )}
            </div>
          </div>

          {/* THE MATCH BAR (the basket). What is ticked, what kind of match it will become
              and — before committing — whether it will tie. The verdict is the point: the
              user sees the accounting consequence rather than discovering it in the totals
              afterwards. Docked to the bottom of the screen while the books are in view, so
              the totals and the Match button never scroll away from the rows. */}
          <div ref={matchbarRef} className="mm-matchbar">
            {workspace && error && <div className="badge badge-risk mm-matchbar-error">{error}</div>}
            <div className="mm-matchbar-row">
              <span className="mm-matchbar-info">
                <span className="muted" style={{ fontVariantNumeric: "tabular-nums" }}>
                  CRI {criPicks.length} · Insurer {insPicks.length}
                </span>
                {/* A basket holding a prior-period item states the PRIOR-PERIOD outcome:
                    it is saved and judged by those rules, never as a main manual match. */}
                {pv ? (
                  <>
                    {pv.kind && <span className="badge">{pv.kindLabel}</span>}
                    {pv.kind ? (
                      <span className="mm-matchbar-figs">
                        <span className="muted">
                          {pv.kind === "cross"
                            ? <>Prior period {fmtAed(pv.carriedTotal)} ⟷ this period {fmtAed(pv.rowTotal)} · gap {fmtResidual(pv.residual)}</>
                            : pv.kind === "xlist"
                              ? <>Insurer list {fmtAed(pv.carriedNet)} + CRI list {fmtAed(pv.rowNet)} · net {fmtResidual(pv.residual)}</>
                            : pv.kind === "basket"
                              ? <>Prior period {fmtAed(pv.carriedNet)} + this period {fmtAed(pv.rowNet)} · net {fmtResidual(pv.residual)}</>
                              : <>net {fmtAed(pv.carriedNet)}</>}
                        </span>
                        <span style={{ color: pv.ties ? "var(--success)" : "var(--warn, var(--risk))" }}>
                          {pv.unreadable
                            ? "an amount on one of these rows could not be read, so the outcome cannot be previewed — the engine decides on save"
                            : pv.signMismatch
                              ? "the amounts agree but the signs do not mirror: on this insurer's books one transaction carries the opposite sign on the other ledger, so this reads as a receipt clearing an invoice — nothing settles: "
                                + (pv.afterPeriod
                                  ? "the item stays open and is annotated"
                                  : `the item and ${pv.nRows > 1 ? `the ${pv.nRows} rows` : "the row"} stay open and move together to Group 3 (Figure / Balance Mismatch), annotated`)
                            : pv.ties
                              ? pv.kind === "offset"
                                ? `✓ nets to zero — all ${pv.nPrior} prior-period items clear together`
                                : pv.kind === "xlist"
                                  ? pv.nPrior > 2 ? `✓ they cancel — all ${pv.nPrior} items clear` : "✓ the two halves cancel — both items clear"
                                  : pv.kind === "basket"
                                    ? `✓ nets to zero — ${pv.nPrior > 1 ? `all ${pv.nPrior} prior-period items clear` : "the prior-period item clears"} and the ${pv.nRows > 1 ? `${pv.nRows} rows move` : "row moves"} to Matched beside ${pv.nPrior > 1 ? "them" : "it"}`
                                  : pv.afterPeriod
                                    ? `✓ ties — ${pv.nRows > 1 ? "these rows are" : "this row is"} dated after the period, so the item stays open and moves to Cleared in Subsequent Month`
                                  : pv.beforePeriod
                                    ? `✓ settles — the item clears and ${pv.nRows > 1 ? "these rows, dated before the period, are" : "this row, dated before the period, is"} listed beside it on Matched`
                                    : `✓ settles — the item clears and ${pv.nRows > 1 ? `the ${pv.nRows} rows move` : "the row moves"} to Matched beside it`
                                    + (pv.mixedTracks ? ` (rows on ${pv.tracks.join(" and ")}: each track's Matched line lists its own rows)` : "")
                              : pv.afterPeriod
                                ? `residual ${fmtResidual(pv.residual)} — outside the ${fmtAed(pv.pairTol)} tolerance, so nothing clears: the item stays open on its group line, annotated with the gap`
                              : pv.kind === "basket"
                                ? `net ${fmtResidual(pv.residual)} — a match across both ledgers must cancel exactly, so nothing settles: the ${pv.nPrior > 1 ? `${pv.nPrior} items` : "item"} and ${pv.nRows > 1 ? `the ${pv.nRows} rows` : "the row"} stay open and move together to Group 3 (Figure / Balance Mismatch), annotated with the gap`
                              : pv.kind === "xlist"
                                ? `net ${fmtResidual(pv.residual)} — ${pv.nPrior > 2 ? "the items" : "the two halves"} do not cancel exactly, so nothing settles: ${pv.nPrior > 2 ? `all ${pv.nPrior}` : "both"} items stay open and move together to Group 3 (Figure / Balance Mismatch), annotated with the gap`
                                : pv.kind === "offset"
                                  ? `net ${fmtResidual(pv.residual)} — prior-period items must cancel exactly, so nothing settles: all ${pv.nPrior} items stay open and move together to Group 3 (Figure / Balance Mismatch), annotated with the gap`
                                  : `residual ${fmtResidual(pv.residual)} — outside the ${fmtAed(pv.pairTol)} tolerance, so nothing settles: the item and ${pv.nRows > 1 ? `the ${pv.nRows} rows` : "the row"} stay open and move together to Group 3 (Figure / Balance Mismatch), annotated with the gap`}
                          {" "}
                          <InfoTip text={pv.kind === "offset"
                            ? "Saved for good: every row comes from the prior-period files, so this re-applies on every future upload. If the items do not cancel exactly, they stay open on Group 3 (Figure / Balance Mismatch). The Closing Balance and the Difference do not change."
                            : pv.kind === "xlist"
                              ? "The two halves clear only if they cancel exactly — opposite signs, the same amount to the cent: the same document sits on opposite books. If they do not, both stay open on Group 3 (Figure / Balance Mismatch). Re-applies while both items are still on their lists. The Closing Balance and the Difference do not change."
                              : pv.kind === "basket"
                                ? "Every item and row counts on its own ledger, and together they must cancel to the cent, on one track. If they do not, they all stay open on Group 3 (Figure / Balance Mismatch). Re-applies on every upload that still lists them all; otherwise the items return to open and the app says why. The Closing Balance and the Difference do not change."
                              : "Re-applies on every upload that still lists this period's transaction; if a later statement no longer carries it, the item returns to open and the app says why. The Closing Balance and the Difference do not change."} />
                        </span>
                        {pv.warning && (
                          <span style={{ color: "var(--warn, var(--risk))" }}>{pv.warning}</span>
                        )}
                      </span>
                    ) : (
                      <span className={pv.refusal ? undefined : "muted"}
                        style={pv.refusal ? { color: "var(--warn, var(--risk))" } : undefined}>
                        {pv.refusal || "Tick the transaction(s) from this period that settled this prior-period item, the matching item on the other book's prior-period list, or more items on its own list to offset them."}
                      </span>
                    )}
                  </>
                ) : (
                <>
                {picked.kind && <span className="badge">{picked.kindLabel}</span>}
                {picked.kind ? (
                  <span className="mm-matchbar-figs">
                    <span className="muted">
                      {picked.crossBook
                        ? <>CRI {fmtAed(picked.criTotal)} ⟷ Insurer {fmtAed(picked.insTotal)} · gap {fmtAed(picked.gap)}</>
                        : <>net {fmtAed(picked.net)}</>}
                    </span>
                    <span style={{
                      color: !offBucket.length && (picked.outcome ? picked.outcome === "match" : picked.ties)
                        ? "var(--success)" : "var(--warn, var(--risk))",
                    }}>
                      {offBucket.length
                        ? `Can't be hand-matched: the basket holds ${offBucket.join("; ")}.`
                        : picked.outcome === "match"
                        ? (picked.residual === 0
                          ? `✓ Ties exactly: all ${picked.total} rows will be marked Matched`
                          : `✓ Within tolerance: the ${picked.crossBook ? "gap" : "net"} of ${fmtAed(picked.residual)} is inside your Premium Matched tolerance of ${fmtAed(amountTol)}, so all ${picked.total} rows will be marked Matched`)
                        : picked.signMismatch
                          ? `Won't tie: ${SIGN_MISMATCH_NOTE} (all ${picked.total} rows)`
                        : picked.outcome === "mismatch"
                          ? `Won't tie: ${picked.residual < 0.005 ? "a rounding remainder under 0.01"
                            : `the ${picked.crossBook ? "gap" : "net"} of ${fmtAed(picked.residual)}`} is above your Premium Matched tolerance of ${fmtAed(amountTol)}, so all ${picked.total} rows will stay visible in Figure/Balance Mismatch`
                          : picked.ties
                            ? `✓ settles exactly — all ${picked.total} rows will be marked Matched`
                            : `residual ${fmtAed(picked.residual)} — Matched if that is within your Matched tolerance, otherwise all ${picked.total} rows stay visible in Figure/Balance Mismatch`}
                      {" "}
                      <InfoTip text="Hand matches are judged against your Premium Matched tolerance, whichever track the rows are on. Either way it is one match, all rows together — and your closing balances and the Difference do not change." />
                    </span>
                  </span>
                ) : (
                  <span className="muted">
                    {picked.total === 1
                      ? "Tick at least one more row to build a match."
                      : "Tick rows in either book, or both, to build a match."}
                  </span>
                )}
                </>
                )}
              </span>
              <span className="mm-matchbar-actions">
                {picked.total > 0 && (
                  <button className="btn" disabled={busy} onClick={clearPicks}>Clear</button>
                )}
                {findFrom && findHits > 0 && (
                  <button className="btn" disabled={busy}
                    title="Show only the rows in the other book with this amount, on either sign. Nothing is matched until you tick rows and press Match."
                    onClick={() => setAmountFind({
                      side: findFrom === "cri" ? "insurer" : "cri",
                      target: findTarget,
                      source: findFrom === "cri" ? criKey : insKey,
                    })}>
                    Find {fmtAed(findTarget)} in {findFrom === "cri" ? "Insurer" : "CRI"}
                    {" "}· {findHits.toLocaleString()} row{findHits === 1 ? "" : "s"}
                  </button>
                )}
                {findFrom && policyTgt && policyHits > 0 && (
                  <button className="btn" disabled={busy}
                    title="Show only the rows in the other book with this policy: the policy keys the matcher pairs on, or the policy number in any column. Nothing is matched until you tick rows and press Match."
                    onClick={() => setPolicyFind({
                      side: findFrom === "cri" ? "insurer" : "cri",
                      target: policyTgt,
                      source: findFrom === "cri" ? criKey : insKey,
                    })}>
                    Find policy {policyTgt.full} in {findFrom === "cri" ? "Insurer" : "CRI"}
                    {" "}· {policyHits.toLocaleString()} row{policyHits === 1 ? "" : "s"}
                  </button>
                )}
                {/* The offer to expand, made the moment the user starts building a match —
                    an offer, never an automatic jump (see openWorkspace). */}
                {picked.total > 0 && !workspace && (
                  <button className="btn" onClick={openWorkspace}
                    title="Continue in the matching workspace: both books over the whole screen, with this selection kept.">
                    Open full screen
                  </button>
                )}
                <button className="btn btn-primary"
                  disabled={busy || (pv ? !pv.pair || !!pv.refusal : !picked.kind || offBucket.length > 0)} onClick={matchPair}>
                  {busy ? "Applying…"
                    : pv ? (pv.kind === "offset" ? `Offset these ${pv.nPrior} prior-period items`
                      : pv.kind ? "Match this item" : "Match")
                    : picked.total < 2 ? "Match"
                      : picked.kind === "same"
                        ? `Offset these ${picked.total} ${picked.sameBookLabel} rows`
                        : `Match these ${picked.total} rows`}
                </button>
              </span>
            </div>

            {trayLegs.length > 0 && (
              <div className="mm-tray" aria-label="Selected rows">
                {trayLegs.map((l) => (
                  <span key={l.side + l.sig} className="mm-pick">
                    <span className="msearch-side">{l.side}</span>
                    <span className="mm-pick-label">{sigLabel(l.frame, l.sig, l.config, l.sigCol)}</span>
                    {l.amount !== null && <span className="msearch-amt">{fmtAed(l.amount)}</span>}
                    <button type="button" className="mm-pick-x" disabled={busy}
                      aria-label="Remove this row from the selection" title="Remove from the selection"
                      onClick={() => l.remove(l.sig)}>×</button>
                  </span>
                ))}
              </div>
            )}

            {/* Within-ledger offsets reclassify real money, so they are called out — but as a
                heads-up read WHILE selecting, not a confirmation that eats a click. */}
            {!pv && picked.kind === "same" && (
              <div className="badge badge-warn" style={{ display: "block", marginTop: 6 }}>
                These {picked.total} rows are all in the {picked.sameBookLabel} ledger, so they will be
                offset against each other rather than matched across the two books.
              </div>
            )}

            {partialCopies.map((c) => (
              <div key={c.sig} className="badge badge-warn" style={{ display: "block", marginTop: 6 }}>
                Near-duplicate: {sigLabel(c.side === "CRI" ? result.df_cri : result.df_insurer, c.sig,
                  c.side === "CRI" ? result.display_config?.cri : result.display_config?.insurer)}
                {" "}has {c.total} identical rows in the {c.side} book and {c.ticked === 1 ? "only 1 is" : `only ${c.ticked} are`} ticked.
                They can only be told apart by their order in the file, so in a later month this
                match might not attach to exactly the same {c.ticked === 1 ? "row" : "rows"}: it may take
                another identical copy, or show as not applied.
              </div>
            ))}
            {priorCopies.map((c) => (
              <div key={c.book + c.sig} className="badge badge-warn" style={{ display: "block", marginTop: 6 }}>
                Identical rows: {sigLabel(carriedRows(prior, c.book), c.sig, priorConfigs[c.book], CARRIED_SIG)} has
                {" "}{c.total} identical rows on the prior-period {c.book === "cri" ? "CRI" : "insurer"} list.
                They can only be told apart by their order in the file, so in a later month this
                match might attach to another identical copy, or show as not applied.
              </div>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* The undo for the last Forget. OUTSIDE the saved list's section: forgetting them
          all empties the list, and an Unmatch from a grid's search panel can forget while
          that section is folded — either way this is precisely when it has to be on screen. */}
      {forgotten && (
        <ForgetUndoBanner count={forgotten.pairs.length} disabled={busy}
          onUndo={() => restoreForgotten(forgotten)} />
      )}

      {saveAsk && (
        <SaveMatchDialog pairs={saveAsk.pairs} checks={saveAsk.checks}
          onConfirm={(a) => { saveAsk.resolve(a); setSaveAsk(null); }}
          onCancel={() => { saveAsk.resolve(null); setSaveAsk(null); }} />
      )}

      {/* Remembered matches — the ONE list of manual matches: the on-disk per-insurer store
          (auto-applied on every upload of this insurer) plus any match made this session
          that the store does not hold (see savedList). */}
      <CollapsibleSection title={`Remembered matches for ${insurerName}`} className="mm-section"
        open={savedOpen} onOpenChange={setSavedOpen}
        badge={rememberedError ? "could not load"
          : nNotApplied ? `${nNotApplied} not applied` : undefined}
        badgeTone="in"
        sub={`${savedList.items.length} match${savedList.items.length === 1 ? "" : "es"}${
          nThisSession ? ` · ${nThisSession} made this session` : ""}`}
        actions={savedOpen && savedList.items.length > 0
          ? <ViewToggle view={rememberedView} onChange={setRememberedView} /> : undefined}>
        <p className="muted" style={{ margin: "0 0 8px" }}>
          Saved matches auto-apply whenever this insurer's SOA is uploaded — rows re-resolve by
          content (amounts included), so a changed figure simply stays open. Every match you make
          is saved as you make it; <strong>Made in</strong> says which were made this session.
          Click a match to see every column of every row it holds.
        </p>
        {rememberedError && (
          // NOT "none saved": the store could not be read, so what it holds is unknown.
          // Say so plainly — the server still applies whatever is in there, so treating
          // this as an empty store would hide an override that is changing the figures.
          <p className="muted" style={{ color: "var(--risk)" }}>
            Could not load the saved matches for this insurer, so this list may be
            incomplete. Any saved match is still being applied to the figures above.
            Reload the page to try again. ({rememberedError})
          </p>
        )}
        {/* Refusals the engine reported that name no match in this list — shown verbatim,
            since folding the old "not applied" list in must not hide any of them. */}
        {savedList.orphans.length > 0 && (
          <div style={{ margin: "0 0 10px" }}>
            <p className="muted" style={{ margin: "0 0 6px", fontSize: "0.78rem" }}>
              The engine also could not apply these saved or selected rows. Nothing was matched
              for them and no figure moved — every row involved stays in its open bucket.
            </p>
            {savedList.orphans.map((u, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 6, marginBottom: 6, fontSize: "0.8rem" }}>
                <span className="muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                  {sigLabel(u.signature.startsWith("INS|") ? insUnmatched : criUnmatched,
                            u.signature,
                            u.signature.startsWith("INS|")
                              ? result.display_config?.insurer
                              : result.display_config?.cri)}
                </span>
                <span className="badge badge-warn" style={{ whiteSpace: "nowrap" }}>
                  {u.side}: {u.reason}
                </span>
              </div>
            ))}
          </div>
        )}
        {savedList.items.length === 0 ? (
          !rememberedError && <p className="muted">None saved yet — matching a pair above remembers it.</p>
        ) : (
          <>
            {/* SPLIT BY OUTCOME, not one mixed list. The two halves need different
                things from the reader — the applied ones are working and only need to
                be findable, the unresolved ones need a decision — and on a store with
                dozens of pairs a per-row badge is the wrong place to carry that, since
                it has to be read row by row to find the handful that failed. Both open
                by default: a saved match that stopped resolving is still silently
                moving nothing while the figures assume it did, so it must never start
                life folded away. In the table the Status column carries that split. */}
            {rememberedView === "table" ? (
              <MatchListTable entries={rememberedEntries} {...listTableFrames}
                title="Remembered matches" resetKey={`${gridResetKey}|remembered`} busy={busy}
                actionLabel="Forget" typedWord="FORGET"
                describe={(n) => `Forget ${n} remembered match${n === 1 ? "" : "es"} for ${insurerName}? They are deleted from the store and stop auto-applying; any applied this run return to their open buckets. Undo is offered only until your next change on this tab.`}
                onAction={(keys) => forgetPairs(byKey(savedList.items, (i) => i.key, keys).map((i) => i.pair))} />
            ) : rememberedSections.map((sec) => (
              <CollapsibleSection key={sec.key} title={sec.title} sub={sec.sub}
                hint={sec.hint} defaultOpen>
                {sec.items.map((it, i) => {
                  const p = it.pair;
                  // The engine's own reason for refusing this pair, when it reported
                  // one. "Ambiguous" and "not present" look identical on the surface
                  // but need different action, so prefer the engine's wording.
                  const why = it.reason;
                  const badge = savedMatchBadge(why);
                  return (
                    <MatchGroupRow key={i} pair={p} {...matchRowProps}
                      actions={
                        <>
                          {/* Only the exception is badged. Inside a section headed
                              "Applied this run", an "Applied this run" chip on every
                              row is noise that makes the failures harder to spot. */}
                          {!sec.applied && (
                            <>
                              <span className="badge badge-warn">{badge.label}</span>
                              <InfoTip text={why
                                ? `${why.side}: ${why.reason}. ${badge.reapplies
                                  ? "It stays saved and will re-apply when a single matching row appears."
                                  : "It stays saved, but it won't re-apply to the rows as they now read."}`
                                : "This saved pair didn't match any row in the files you uploaded this time — usually because an amount or reference changed. It stays saved and will re-apply when matching rows appear."} />
                            </>
                          )}
                          {it.rolled && (
                            <>
                              <span className="badge">Prior-period items · {it.rolled}</span>
                              <InfoTip text="Its rows have rolled forward into the prior-period lists, so this match applied to those items instead." />
                            </>
                          )}
                          {!it.saved ? (
                            <>
                              <span className="badge badge-warn">Not saved</span>
                              <InfoTip text="Saving this match failed, so it applies for this session only and will not re-apply on the next upload. Forget it and match the rows again to retry the save." />
                            </>
                          ) : it.thisSession && <span className="badge">This session</span>}
                          <button className="btn" disabled={busy} onClick={() => forgetPairs([p])}>Forget</button>
                        </>
                      } />
                  );
                })}
              </CollapsibleSection>
            ))}
            {/* Two-step and typed. This erases every pairing saved for this insurer, it
                cannot be undone from the UI, and the button sits directly under a list of
                single Forget buttons — the easiest mis-click in the tab. */}
            {remembered.length > 0 && (
              <ConfirmDangerAction
                label="Forget all remembered matches"
                disabled={busy}
                title={`Forget all ${remembered.length} remembered match${remembered.length === 1 ? "" : "es"} for ${insurerName}?`}
                body={<>
                  Every saved pairing for this insurer is deleted from the store — including
                  ones built in earlier months — and none of them will auto-apply on future
                  uploads. Any applied in this run are released back to their open buckets
                  immediately. <strong>Undo is offered here only until your next change on this
                  tab, and never after a reload — after that it cannot be reversed.</strong> To
                  remove just one, use its own Forget button above.
                </>}
                confirmWord="FORGET"
                confirmLabel={`Forget all ${remembered.length}`}
                onConfirm={() => forgetPairs(remembered)} />
            )}
          </>
        )}
      </CollapsibleSection>

      {/* Prior-period matches: their own store, outcomes and clear review. Matching them
          happens in the books above. */}
      {result.unreconciled && prior && (
        <PriorPeriodMatches block={prior} insurerName={insurerName}
          criFrame={result.df_cri} insurerFrame={result.df_insurer}
          criConfig={result.display_config?.cri} insConfig={result.display_config?.insurer}
          storeVersion={priorStoreVersion} busy={busy}
          onChanged={async () => { await onCarriedPairsChanged?.(); }} />
      )}

      {/* Released auto-matches. A release must never be an invisible permanent change:
          it is listed here with Restore, so a wrong release is one click from undone and
          nobody inherits a mystery override next month. */}
      {(released.length > 0 || releasedError) && (
        <CollapsibleSection title="Released matches" className="mm-section"
          open={releasedOpen} onOpenChange={setReleasedOpen}
          badge={releasedError ? "could not load"
            : nReleasedInert ? `${nReleasedInert} not applied` : undefined}
          sub={`${released.length} match${released.length === 1 ? "" : "es"}`}
          actions={releasedOpen
            ? <ViewToggle view={releasedView} onChange={setReleasedView} /> : undefined}>
          <p className="muted" style={{ margin: "0 0 8px", fontSize: "0.78rem" }}>
            These pairs were matched automatically, but you released them so both rows
            return to the lists above for manual pairing. Your closing balances and the
            Difference are unchanged. Restore puts the automatic match back. An entry
            marked <strong>Not applied</strong> freed nothing this run (the row is matched
            by hand, by the prior-period step, or is not in this upload) — Restore simply
            removes it.
          </p>
          {releasedError && (
            // NOT "none released": what the store holds is unknown, and the server still
            // applies every stored release, so say so rather than show an empty list.
            <p className="muted" style={{ color: "var(--risk)" }}>
              Could not load the released matches for this insurer, so this list may be
              incomplete. Any released match is still being applied to the figures above.
              Reload the page to try again. ({releasedError})
            </p>
          )}
          {releasedView === "table" ? (
            <MatchListTable entries={releasedEntries} {...listTableFrames}
              title="Released matches" resetKey={`${gridResetKey}|released`} busy={busy}
              actionLabel="Restore"
              describe={(n) => `Restore ${n} released match${n === 1 ? "" : "es"}? The app matches those rows automatically again. Totals and the Difference do not change.`}
              onAction={(keys) => restoreMatches(byKey(released, releasedKey, keys))} />
          ) : released.map((g, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: 6, marginBottom: 6, fontSize: "0.8rem" }}>
              {/* Each leg is BADGED with its book. This panel sits below both grids with
                  no surrounding context, and a released same-book group has both legs on
                  ONE side — so an unlabelled "A ⟷ B" actively misreads as CRI-vs-insurer.
                  Finance has to be able to tell which ledger each row came from. */}
              <span className="muted" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                {g.sigs.map((s, j) => {
                  const isIns = s.startsWith("INS|");
                  return (
                    <span key={s}>
                      {j > 0 && "  +  "}
                      {sigSide(s) && <span className="msearch-side">{sigSide(s)}</span>}
                      {sigLabel(
                        isIns ? result.df_insurer : result.df_cri, s,
                        isIns ? result.display_config?.insurer : result.display_config?.cri,
                      )}
                      {/* The amount, which sigLabel omits — without it a released pair
                          cannot be judged (do these two sides actually offset?). */}
                      {sigAmount(isIns ? result.df_insurer : result.df_cri, s) !== null && (
                        <span className="msearch-amt">
                          {fmtAed(sigAmount(isIns ? result.df_insurer : result.df_cri, s))}
                        </span>
                      )}
                    </span>
                  );
                })}
              </span>
              {releasedInert(g) && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
                  <span className="badge badge-warn">Not applied</span>
                  <InfoTip text={`Nothing was released this run: ${releasedInert(g)!.reason}.`} />
                </span>
              )}
              <button className="btn" disabled={busy} onClick={() => restoreMatch(g)}>
                Restore
              </button>
            </div>
          ))}
        </CollapsibleSection>
      )}
    </div>
  );
}

// List | Table switch for a stored-match panel — same look as the suggestion card's.
function ViewToggle({ view, onChange }: {
  view: "list" | "table"; onChange: (v: "list" | "table") => void;
}) {
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {(["list", "table"] as const).map((v) => (
        <button key={v} className={view === v ? "btn btn-primary" : "btn"}
          style={{ fontSize: "0.75rem", padding: "4px 10px", textTransform: "capitalize" }}
          onClick={() => onChange(v)}>
          {v}
        </button>
      ))}
    </span>
  );
}
